import React, { useState, useMemo, useCallback, useEffect, useRef, Suspense } from 'react';
import { Client, ClientStatus, UserRole, TaskStatus, OnboardingTasks, OnboardingTask, RemarkAction, ActivityLog, FileAttachment, User } from './types';
import { INITIAL_ACTIVITY_LOGS, TASK_LABELS } from './constants';
import ClientDetailsModal from './components/ClientDetailsModal';
import RoleSwitcher from './components/RoleSwitcher';
import { InboxIcon, HistoryIcon, CalendarIcon, UsersIcon, WhatsAppIcon, TrendingUpIcon, CogIcon, BuildingIcon, FileTextIcon, UserIcon, GlobeIcon, TicketIcon, CodeIcon, ChartBarIcon, DollarSignIcon, ListTodoIcon, CreditCardIcon, SparklesIcon, MoonIcon, SunIcon, MonitorIcon, StarIcon, PinIcon, PhoneIcon } from './components/ui/icons';
import RemarkModal from './components/RemarkModal';
import LoginForm from './components/LoginForm';
import PasskeySetupPage from './components/PasskeySetupPage';
import LoadingScreen from './components/LoadingScreen';
import VersionChecker from './components/VersionChecker';
import Modal from './components/ui/Modal';
import ChangePasswordForm from './components/ChangePasswordForm';
import NotificationsMenu from './components/NotificationsMenu';
import TasksMeetingsQuickAccess from './components/TasksMeetingsQuickAccess';
import RemVoiceDialer from './components/RemVoiceDialer';
import { RemVoiceProvider } from './contexts/RemVoiceContext';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation, Navigate, useParams } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { PermissionProvider, usePermission } from './services/PermissionContext';
import { hasListedPermission } from './services/recruitmentPermissionAliases';
import { ToastProvider } from './components/ui/ToastContainer';
import { CompanyProvider } from './contexts/CompanyContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ThemeProvider, useTheme, ThemePreference } from './contexts/ThemeContext';
import {
  buildMenuRegistry,
  flattenMenuNavigation,
  MenuNavEntry,
  normalizeMenuPath,
  recordMenuVisit,
  resolveMostUsedMenuEntries,
  resolvePinnedMenuEntries,
  togglePinnedMenuPath,
} from './utils/menuUsage';
import {
  Dashboard,
  CreateClientWizardPage,
  ClientsPage,
  ClientsV2Page,
  ClientsV2StatusCheckPage,
  ClientsRetentionsPage,
  ClientDetailPage,
  ContractAttachmentViewerPage,
  AgentsPage,
  AgentDetailPage,
  ActivityPage,
  CalendarPage,
  AdminUserManagement,
  UserSalesSummary,
  SalesSummaryV2,
  CommissionManagement,
  WebManagerClientsPage,
  WebManagerClientDetailPage,
  ModificationRequestDetailPage,
  ContractGenerator,
  ContractTemplateAdmin,
  SavedContracts,
  ContractDetailPage,
  ProposalGenerator,
  SavedProposals,
  EmailManagement,
  SuccessCriteriaDashboard,
  RoleManagement,
  DatabaseBackupManager,
  AISettingsPage,
  AIQuerySettingsPage,
  RAGManagementPage,
  CompanySettingsPage,
  CompanySettings,
  S3BrowserPage,
  PhoneParserToolPage,
  LocationsFetcherPage,
  SystemHealthPage,
  VpnAdminPage,
  WhatsAppPage,
  SuperadminDashboard,
  InvestorDashboard,
  ProfilePage,
  ComparisonGraphs,
  AgentPerformanceReport,
  TicketsPage,
  TicketDetailPage,
  TicketAnalytics,
  PaymentAnalytics,
  PaymentAnalyticsV2,
  TicketSettingsPage,
  UnlinkedTicketsPage,
  TicketApiDocPage,
  SalaryListPage,
  SalaryConfigPage,
  SalaryDashboardPage,
  AdvancesPage,
  SalaryAccountingSettings,
  MeetingAgentSettingsPage,
  MeetingsCalendarView,
  MyMeetingsPage,
  MeetingDetailPage,
  MeetingPermissionsPage,
  PublicRequirementPage,
  PublicModificationPage,
  PublicReviewForm,
  MeetingReviewPage,
  SupportAutomationFlowBuilder,
  SupportAgentsList,
  SupportAutomationSettings,
  SupportAutomationClientConfig,
  WhatsAppSupportPage,
  SupportConversationHistory,
  SupportReportPage,
  AccountingDashboard,
  ChartOfAccounts,
  JournalEntries,
  IncomeRecording,
  ExpenseRecording,
  ARInvoices,
  BalanceSheet,
  IncomeStatement,
  TrialBalance,
  GeneralLedger,
  CurrencyManagement,
  AccountingPeriods,
  AccountingPermissions,
  SpecialTransactions,
  ProfitLossStatement,
  ReconciliationReport,
  DailyActivityReport,
  AIChatInterface,
  VoipCallLogsPage,
  VoipAgentLogsPage,
  TasksPage,
  DemoRequestsPage,
  CEODashboard,
  OwnerDashboardPage,
  CandidatesPage,
  CandidateDetailPage,
  JobPostingsPage,
  RecruitmentPipelinePage,
  RecruitmentSettingsPage,
  PublicCareersPage,
  ImpersonateLoginPage,
  CustomerSubscriptionPortal,
  RecruitmentReportsPage,
  MeetingDistributionPage,
  MeetingBookingPage,
  AssignMeetingsPage,
  WhatsAppAnalytics,
  CampaignListPage,
  CampaignDetailPage,
  APIManagement,
  DevTrackerSettings,
  DevTaskRequests,
  DevKanbanBoard,
  DevReleases,
  DevDashboard,
  DevProductDetail,
  AttendanceDashboardPage,
  AttendanceRecordsPage,
  AttendanceReportsPage,
  AttendanceSettingsPage,
  AttendanceTimeChangeRequestsPage,
  StripeSettingsPage,
  StripeSubscriptionsPage,
  StripeCustomersPage,
  StripeTransactionsPage,
  StripePaymentLinksPage,
  StripeReconciliationPage,
  StripeBillingOrphansPage,
  SubscriptionsDashboard,
  PackagesManagement,
  AddonsManagement,
  CouponsManagement,
  ClientSubscriptionsPage,
  ActiveSubscriptionsAudit,
  ClientSubscriptionDetail,
  SubscriptionReports,
  NewSubscription,
  SubscriptionAnalytics,
  CrmLinks,
  CrmUsageAnalytics,
  CrmLeadAnalytics,
  CrmCommunicationAnalytics,
  CrmPropertyAnalytics,
  CrmSubscriptionAnalytics,
  CrmSearchAnalytics,
  CrmTeamAnalytics,
  CrmGrowthAnalytics,
} from './lazyPages';

const RouteFallback: React.FC = () => (
  <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
);

// Stable wrapper component (defined at module scope to avoid remounts on App re-render)
type ClientDetailWrapperProps = {
  clients: Client[];
  activityLogs: any[];
  currentUserRoles: string[];
  onEdit: (client: Client) => void;
  currentUsername: string | null;
  currentUserName: string | null;
  onDelete: (client: Client) => void;
};

const ClientDetailWrapper: React.FC<ClientDetailWrapperProps> = React.memo(({ clients, activityLogs, currentUserRoles, onEdit, currentUsername, currentUserName, onDelete }) => {
  const { id } = useParams<{ id: string }>();

  // Memoize client to prevent unnecessary computations
  const client = useMemo(() => clients.find(c => c.id === id), [clients, id]);
  const handleBack = useCallback(() => window.history.back(), []);
  const memoizedUserRoles = useMemo(() => currentUserRoles, [currentUserRoles.join(',')]);

  if (!client) return <div>Client not found</div>;

  return (
    <ClientDetailPage
      client={client}
      activityLogs={activityLogs}
      onBack={handleBack}
      onEdit={onEdit}
      onDelete={onDelete}
      currentUserRoles={memoizedUserRoles}
      currentUsername={currentUsername}
      currentUserName={currentUserName}
    />
  );
}, (prevProps, nextProps) => {
  const urlId = window.location.pathname.split('/clients/')[1]?.split('/')[0];
  if (!urlId) return false;

  const prevClient = prevProps.clients.find(c => c.id === urlId);
  const nextClient = nextProps.clients.find(c => c.id === urlId);

  if (!prevClient && !nextClient) return true;
  if (!prevClient || !nextClient) return false;

  const sameUsername = prevProps.currentUsername === nextProps.currentUsername;
  const sameUserName = prevProps.currentUserName === nextProps.currentUserName;
  const sameRoles = prevProps.currentUserRoles.join(',') === nextProps.currentUserRoles.join(',');
  const sameClient = JSON.stringify(prevClient) === JSON.stringify(nextClient);

  return sameUsername && sameUserName && sameRoles && sameClient;
});
// Wrapper for WebManagerClientDetailPage with URL routing (module scope to avoid remounts on App re-render)
interface WebManagerClientDetailWrapperProps {
  currentUserRoles: string[];
}

const WebManagerClientDetailWrapper: React.FC<WebManagerClientDetailWrapperProps> = ({ currentUserRoles }) => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  if (!clientId) return <div>Client ID not found</div>;
  return <WebManagerClientDetailPage clientId={clientId} onBack={() => navigate('/web-manager-clients')} currentUserRoles={currentUserRoles} />;
};

// Logo that switches between light/dark versions
const ThemeLogo: React.FC = () => {
  const { isDark } = useTheme();
  return (
    <img
      src={isDark ? "/rem-logo-gold.png" : "https://webfiles.remapp.ae/web_remapp_ae/hjas-1-1.png"}
      alt="REM Clients Logo"
      style={{ height: 36, width: 'auto', marginRight: 8 }}
    />
  );
};

// Public Review Form Wrapper
const PublicReviewFormWrapper: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  if (!token) return <div className="p-8 text-center text-red-500">Invalid review link</div>;
  return <PublicReviewForm token={token} />;
};

// Theme selector (uses ThemeContext)
const THEME_OPTIONS: { value: ThemePreference; label: string; icon: React.FC<{ className?: string }> }[] = [
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon },
  { value: 'auto', label: 'System', icon: MonitorIcon },
];

const DarkModeToggle: React.FC = () => {
  const { theme, setTheme, isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const activeOption = THEME_OPTIONS.find(option => option.value === theme) ?? THEME_OPTIONS[0];
  const ActiveIcon = activeOption.icon;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="relative p-2 rounded-lg transition-colors text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        title={`Theme: ${activeOption.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ActiveIcon className="w-5 h-5" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-36 rounded-lg border border-slate-200 bg-white py-1 shadow-lg z-50 dark:border-slate-700 dark:bg-slate-800"
        >
          {THEME_OPTIONS.map(option => {
            const OptionIcon = option.icon;
            const isActive = theme === option.value;
            return (
              <button
                key={option.value}
                role="menuitem"
                onClick={() => {
                  setTheme(option.value);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700'
                }`}
              >
                <OptionIcon className="w-4 h-4" />
                <span>{option.label}</span>
                {option.value === 'auto' && (
                  <span className="ml-auto text-xs text-slate-400 dark:text-slate-500">
                    {isDark ? 'Dark' : 'Light'}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface RemarkModalState {
  isOpen: boolean;
  taskKey: keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'> | null;
  clientId: string | null;
  newStatus: TaskStatus | null;
  action: RemarkAction | null;
}

// Per-menu soft color styles for icons, hover, and active states
const MENU_STYLES: Record<string, { icon: string; hover: string; active: string; subActive: string; bullet: string }> = {
  'Dashboard': { icon: 'text-sky-600', hover: 'hover:bg-sky-50', active: 'bg-sky-600 text-white', subActive: 'bg-sky-100 text-sky-700', bullet: 'bg-sky-400' },
  'Investor Dashboard': { icon: 'text-purple-600', hover: 'hover:bg-purple-50', active: 'bg-purple-600 text-white', subActive: 'bg-purple-100 text-purple-700', bullet: 'bg-purple-400' },
  'Clients': { icon: 'text-emerald-600', hover: 'hover:bg-emerald-50', active: 'bg-emerald-600 text-white', subActive: 'bg-emerald-100 text-emerald-700', bullet: 'bg-emerald-400' },
  'Success Criteria': { icon: 'text-amber-600', hover: 'hover:bg-amber-50', active: 'bg-amber-600 text-white', subActive: 'bg-amber-100 text-amber-700', bullet: 'bg-amber-400' },
  'Tickets': { icon: 'text-rose-600', hover: 'hover:bg-rose-50', active: 'bg-rose-600 text-white', subActive: 'bg-rose-100 text-rose-700', bullet: 'bg-rose-400' },

  'Attendance': { icon: 'text-indigo-600', hover: 'hover:bg-indigo-50', active: 'bg-indigo-600 text-white', subActive: 'bg-indigo-100 text-indigo-700', bullet: 'bg-indigo-400' },
  'Salaries': { icon: 'text-lime-600', hover: 'hover:bg-lime-50', active: 'bg-lime-600 text-white', subActive: 'bg-lime-100 text-lime-700', bullet: 'bg-lime-400' },
  'Settings': { icon: 'text-slate-600', hover: 'hover:bg-slate-50', active: 'bg-slate-700 text-white', subActive: 'bg-slate-100 text-slate-700', bullet: 'bg-slate-400' },
  'Calendar': { icon: 'text-indigo-600', hover: 'hover:bg-indigo-50', active: 'bg-indigo-600 text-white', subActive: 'bg-indigo-100 text-indigo-700', bullet: 'bg-indigo-400' },
  'WhatsApp': { icon: 'text-green-600', hover: 'hover:bg-green-50', active: 'bg-green-600 text-white', subActive: 'bg-green-100 text-green-700', bullet: 'bg-green-400' },
  'Mailbox': { icon: 'text-sky-600', hover: 'hover:bg-sky-50', active: 'bg-sky-600 text-white', subActive: 'bg-sky-100 text-sky-700', bullet: 'bg-sky-400' },
  'Tasks': { icon: 'text-violet-600', hover: 'hover:bg-violet-50', active: 'bg-violet-600 text-white', subActive: 'bg-violet-100 text-violet-700', bullet: 'bg-violet-400' },
  'Meetings': { icon: 'text-purple-600', hover: 'hover:bg-purple-50', active: 'bg-purple-600 text-white', subActive: 'bg-purple-100 text-purple-700', bullet: 'bg-purple-400' },
  'Accounting': { icon: 'text-cyan-600', hover: 'hover:bg-cyan-50', active: 'bg-cyan-600 text-white', subActive: 'bg-cyan-100 text-cyan-700', bullet: 'bg-cyan-400' },
  'Reports': { icon: 'text-orange-600', hover: 'hover:bg-orange-50', active: 'bg-orange-600 text-white', subActive: 'bg-orange-100 text-orange-700', bullet: 'bg-orange-400' },
  'Subscriptions': { icon: 'text-pink-600', hover: 'hover:bg-pink-50', active: 'bg-pink-600 text-white', subActive: 'bg-pink-100 text-pink-700', bullet: 'bg-pink-400' },
  'Marketing Campaigns': { icon: 'text-green-600', hover: 'hover:bg-green-50', active: 'bg-green-600 text-white', subActive: 'bg-green-100 text-green-700', bullet: 'bg-green-400' },
  'Dev Tracker': { icon: 'text-blue-600', hover: 'hover:bg-blue-50', active: 'bg-blue-600 text-white', subActive: 'bg-blue-100 text-blue-700', bullet: 'bg-blue-400' },
  'Stripe': { icon: 'text-violet-600', hover: 'hover:bg-violet-50', active: 'bg-violet-600 text-white', subActive: 'bg-violet-100 text-violet-700', bullet: 'bg-violet-400' },
  'VOIP': { icon: 'text-teal-600', hover: 'hover:bg-teal-50', active: 'bg-teal-600 text-white', subActive: 'bg-teal-100 text-teal-700', bullet: 'bg-teal-400' },
  'Recruitment': { icon: 'text-fuchsia-600', hover: 'hover:bg-fuchsia-50', active: 'bg-fuchsia-600 text-white', subActive: 'bg-fuchsia-100 text-fuchsia-700', bullet: 'bg-fuchsia-400' },
};

const getMenuStyle = (label: string) => MENU_STYLES[label] || { icon: 'text-primary-600', hover: 'hover:bg-primary-50', active: 'bg-primary-600 text-white', subActive: 'bg-primary-100 text-primary-700', bullet: 'bg-primary-400' };

const MENU_ITEMS = [
  {
    label: 'Dashboard',
    to: '/dashboard',
    icon: GlobeIcon,
    permission: { module: 'Dashboard', section: 'Main', action: 'view' },
  },
  {
    label: 'Clients',
    to: '/clients',
    icon: BuildingIcon,
    permission: { module: 'Clients', section: 'ClientList', action: 'view' },
    submenu: [
      {
        label: 'Client List',
        to: '/clients',
        permission: { module: 'Clients', section: 'ClientList', action: 'view' },
      },
      {
        label: 'Client List v2',
        to: '/clients/v2',
        permission: { module: 'Clients', section: 'ClientList', action: 'view' },
      },
      {
        label: 'Clients Retentions',
        to: '/clients/retentions',
        permission: { module: 'Clients', section: 'ClientList', action: 'view' },
      },
      {
        label: 'Web Clients',
        to: '/web-manager-clients',
        permission: { module: 'Clients', section: 'WebClients', action: 'view' },
      },
      {
        label: 'Agents',
        to: '/agents',
        permission: { module: 'Clients', section: 'ClientList', action: 'view' },
      },
      {
        label: 'Generate Contract',
        to: '/contracts',
        permission: { module: 'Contracts', section: 'ContractGenerator', action: 'view' },
      },
      {
        label: 'Saved Contracts',
        to: '/contracts/saved',
        permission: { module: 'Contracts', section: 'SavedContracts', action: 'view' },
      },
      {
        label: 'Demo Requests',
        to: '/demo-requests',
        permission: { module: 'DemoRequests', section: 'Main', action: 'view' },
      },
      {
        label: 'Generate Proposal',
        to: '/proposals',
        permission: { module: 'Contracts', section: 'ContractGenerator', action: 'view' },
      },
      {
        label: 'Saved Proposals',
        to: '/proposals/saved',
        permission: { module: 'Contracts', section: 'SavedContracts', action: 'view' },
      },
    ],
  },

  {
    label: 'Tickets',
    to: '/tickets',
    icon: TicketIcon,
    permission: { module: 'Tickets', section: 'Main', action: 'view' },
    submenu: [
      {
        label: 'All Tickets',
        to: '/tickets',
        permission: { module: 'Tickets', section: 'Main', action: 'view' },
      },
      {
        label: 'Settings',
        to: '/tickets/settings',
        permission: { module: 'Tickets', section: 'Settings', action: 'view' },
      },
      {
        label: 'Unlinked Tickets',
        to: '/tickets/unlinked',
        permission: { module: 'Tickets', section: 'Main', action: 'view' },
      },
      {
        label: 'Tickets API',
        to: '/tickets/api',
        permission: { module: 'Tickets', section: 'Settings', action: 'view' },
      },
    ],
  },

  {
    label: 'Recruitment',
    to: '/recruitment/candidates',
    icon: UsersIcon,
    permission: { module: 'Recruitment', section: 'Candidates', action: 'view' },
    submenu: [
      { label: 'Candidates', to: '/recruitment/candidates', permission: { module: 'Recruitment', section: 'Candidates', action: 'view' } },
      { label: 'Jobs', to: '/recruitment/jobs', permission: { module: 'Recruitment', section: 'Jobs', action: 'view' } },
      { label: 'Pipeline', to: '/recruitment/pipeline', permission: { module: 'Recruitment', section: 'Pipeline', action: 'view' } },
      { label: 'Reports', to: '/recruitment/reports', permission: { module: 'Recruitment', section: 'Reports', action: 'view' } },
      { label: 'Settings', to: '/recruitment/settings', permission: { module: 'Recruitment', section: 'Settings', action: 'view' } },
    ],
  },
  {
    label: 'Attendance',
    to: '/hr/attendance',
    icon: CalendarIcon,
    permission: { module: 'HR', section: 'Attendance', action: 'view' },
    submenu: [
      { label: 'Dashboard', to: '/hr/attendance', permission: { module: 'HR', section: 'Attendance', action: 'view' } },
      { label: 'Records', to: '/hr/attendance/records', permission: { module: 'HR', section: 'Attendance', action: 'view' } },
      { label: 'Reports', to: '/hr/attendance/reports', permission: { module: 'HR', section: 'Attendance', action: 'view' } },
      { label: 'Settings', to: '/hr/attendance/settings', permission: { module: 'HR', section: 'Attendance', action: 'manage_rules' } },
      { label: 'Time Requests', to: '/hr/attendance/time-requests', permission: { module: 'HR', section: 'Attendance', action: 'view' } },
    ],
  },
  {
    label: 'Salaries',
    to: '/salaries',
    icon: DollarSignIcon,
    permission: { module: 'Salaries', section: 'SalaryList', action: 'view' },
    submenu: [
      { label: 'Dashboard', to: '/salaries/dashboard', permission: { module: 'Salaries', section: 'SalaryList', action: 'view' } },
      { label: 'Salary List', to: '/salaries', permission: { module: 'Salaries', section: 'SalaryList', action: 'view' } },
      { label: 'Employee Config', to: '/salaries/config', permission: { module: 'Salaries', section: 'Config', action: 'view' } },
      { label: 'Advances', to: '/salaries/advances', permission: { module: 'Salaries', section: 'Advances', action: 'view' } },
      { label: 'Accounting Settings', to: '/salaries/accounting', permission: { module: 'Salaries', section: 'Accounting', action: 'view' } },
    ],
  },
  {
    label: 'Accounting',
    to: '/accounting',
    icon: DollarSignIcon,
    permission: { module: 'Accounting', section: 'Reports', action: 'viewDashboard' },
    submenu: [
      {
        label: 'Dashboard',
        to: '/accounting/dashboard',
        permission: { module: 'Accounting', section: 'Reports', action: 'viewDashboard' },
      },
      {
        label: 'Journal Entries',
        to: '/accounting/journal-entries',
        permission: { module: 'Accounting', section: 'JournalEntries', action: 'view' },
      },
      {
        label: 'Special Transactions',
        to: '/accounting/special-transactions',
        permission: { module: 'Accounting', section: 'JournalEntries', action: 'view' },
      },
      {
        label: 'Income Recording',
        to: '/accounting/income',
        permission: { module: 'Accounting', section: 'Income', action: 'record' },
      },
      {
        label: 'Expense Recording',
        to: '/accounting/expenses',
        permission: { module: 'Accounting', section: 'Expenses', action: 'record' },
      },
      {
        label: 'Settings',
        permission: { module: 'Accounting', section: 'Accounts', action: 'view' },
        submenu: [
          {
            label: 'Chart of Accounts',
            to: '/accounting/accounts',
            permission: { module: 'Accounting', section: 'Accounts', action: 'view' },
          },
          {
            label: 'Currencies & Rates',
            to: '/accounting/currencies',
            permission: { module: 'Accounting', section: 'Currencies', action: 'view' },
          },
          {
            label: 'Accounting Periods',
            to: '/accounting/periods',
            permission: { module: 'Accounting', section: 'Periods', action: 'view' },
          },
          {
            label: 'Permissions',
            to: '/accounting/permissions',
            permission: { module: 'Accounting', section: 'Permissions', action: 'manage' },
          },
          {
            label: 'Company Management',
            to: '/settings/companies',
            permission: { module: 'Superadmin', section: 'CompanySettings', action: 'view' },
          },
        ],
      },
      {
        label: 'Reports',
        permission: { module: 'Accounting', section: 'Reports', action: 'viewARInvoices' },
        submenu: [
          {
            label: 'AR Invoices',
            to: '/accounting/ar-invoices',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewARInvoices' },
          },
          {
            label: 'Profit & Loss Statement',
            to: '/accounting/profit-loss',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewProfitLoss' },
          },
          {
            label: 'Reconciliation Report',
            to: '/accounting/reconciliation',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewProfitLoss' },
          },
          {
            label: 'Daily Activity',
            to: '/accounting/daily-activity',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewDailyActivity' },
          },
          {
            label: 'Balance Sheet',
            to: '/accounting/balance-sheet',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewBalanceSheet' },
          },
          {
            label: 'Income Statement',
            to: '/accounting/income-statement',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewIncomeStatement' },
          },
          {
            label: 'Trial Balance',
            to: '/accounting/trial-balance',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewTrialBalance' },
          },
          {
            label: 'General Ledger',
            to: '/accounting/general-ledger',
            permission: { module: 'Accounting', section: 'Reports', action: 'viewGeneralLedger' },
          },
        ],
      },
    ],
  },
  {
    label: 'Stripe',
    to: '/stripe',
    icon: CreditCardIcon,
    permission: { module: 'Stripe', section: 'Settings', action: 'view' },
    submenu: [
      {
        label: 'Stripe Settings',
        to: '/stripe/settings',
        permission: { module: 'Stripe', section: 'Settings', action: 'view' },
      },
      {
        label: 'Subscriptions',
        to: '/stripe/subscriptions',
        permission: { module: 'Stripe', section: 'Subscriptions', action: 'view' },
      },
      {
        label: 'Stripe Customers',
        to: '/stripe/customers',
        permission: { module: 'Stripe', section: 'Customers', action: 'view' },
      },
      {
        label: 'Transactions',
        to: '/stripe/transactions',
        permission: { module: 'Stripe', section: 'Transactions', action: 'view' },
      },
      {
        label: 'Payment Links',
        to: '/stripe/payment-links',
        permission: { module: 'Stripe', section: 'PaymentLinks', action: 'view' },
      },
      {
        label: 'Stripe Reconciliation',
        to: '/stripe/reconciliation',
        permission: { module: 'Stripe', section: 'Reconciliation', action: 'view' },
      },
      {
        label: 'Billing Orphans',
        to: '/stripe/billing-orphans',
        permission: { module: 'Stripe', section: 'Transactions', action: 'view' },
      },
    ],
  },
  {
    label: 'VOIP',
    to: '/voip/calls',
    icon: PhoneIcon,
    permission: { module: 'VOIP', section: 'Main', action: 'view' },
    submenu: [
      {
        label: 'Call Logs',
        to: '/voip/calls',
        permission: { module: 'VOIP', section: 'CallLog', action: 'view' },
      },
      {
        label: 'Agent Logs',
        to: '/voip/agents',
        permission: { module: 'VOIP', section: 'AgentLog', action: 'view' },
      },
    ],
  },
  {
    label: 'Subscriptions',
    to: '/subscriptions',
    icon: CreditCardIcon,
    permission: { module: 'Subscriptions', section: 'Main', action: 'view' },
    submenu: [
      {
        label: 'Dashboard',
        to: '/subscriptions',
        permission: { module: 'Subscriptions', section: 'Main', action: 'view' },
      },
      {
        label: 'Client Subscriptions',
        to: '/subscriptions/subscriptions',
        permission: { module: 'Subscriptions', section: 'Subscriptions', action: 'view' },
      },
      {
        label: 'Active Subscriptions Audit',
        to: '/subscriptions/audit',
        permission: { module: 'Subscriptions', section: 'Subscriptions', action: 'view' },
      },
      {
        label: 'Packages',
        to: '/subscriptions/packages',
        permission: { module: 'Subscriptions', section: 'Packages', action: 'view' },
      },
      {
        label: 'Add-ons',
        to: '/subscriptions/addons',
        permission: { module: 'Subscriptions', section: 'Addons', action: 'view' },
      },
      {
        label: 'Coupons',
        to: '/subscriptions/coupons',
        permission: { module: 'Subscriptions', section: 'Coupons', action: 'view' },
      },
      {
        label: 'Reports',
        to: '/subscriptions/reports',
        permission: { module: 'Subscriptions', section: 'Reports', action: 'view' },
      },
      {
        label: 'Analytics',
        to: '/subscriptions/analytics',
        permission: { module: 'Subscriptions', section: 'Reports', action: 'view' },
      },
      {
        label: 'CRM Links',
        to: '/subscriptions/crm-links',
        permission: { module: 'Subscriptions', section: 'Reports', action: 'view' },
      },
      {
        label: 'API Management',
        to: '/subscriptions/api',
        permission: { module: 'Subscriptions', section: 'API', action: 'view' },
      },
    ],
  },
  {
    label: 'Marketing Campaigns',
    to: '/marketing/campaigns',
    icon: SparklesIcon,
    permission: { module: 'Marketing', section: 'Campaigns', action: 'view' },
    submenu: [
      { label: 'All Campaigns', to: '/marketing/campaigns', permission: { module: 'Marketing', section: 'Campaigns', action: 'view' } },
      { label: 'New Campaign', to: '/marketing/campaigns/new', permission: { module: 'Marketing', section: 'Campaigns', action: 'create' } },
    ],
  },
  {
    label: 'Development',
    to: '/development/dashboard',
    icon: CodeIcon,
    permission: { module: 'Superadmin', section: 'DevTracker', action: 'view' },
    submenu: [
      { label: 'Dashboard', to: '/development/dashboard', permission: { module: 'Superadmin', section: 'DevTracker', action: 'view' } },
      { label: 'Requests', to: '/development/requests', permission: { module: 'Superadmin', section: 'DevTracker', action: 'view' } },
      { label: 'Board', to: '/development/board', permission: { module: 'Superadmin', section: 'DevTracker', action: 'view' } },
      { label: 'Releases', to: '/development/releases', permission: { module: 'Superadmin', section: 'DevTracker', action: 'view' } },
      { label: 'Settings', to: '/development/settings', permission: { module: 'Superadmin', section: 'DevTracker', action: 'view' } },
    ],
  },
  {
    label: 'Meetings',
    to: '/meetings/distribute',
    icon: UsersIcon,
    permission: { module: 'Meetings', section: 'Distribution', action: 'view' },
    submenu: [
      { label: 'Distribute Meeting', to: '/meetings/distribute', permission: { module: 'Meetings', section: 'Distribution', action: 'create' } },
      { label: 'Book Meeting', to: '/meetings/book', permission: { module: 'Meetings', section: 'Booking', action: 'create' } },
      { label: 'Assign Meetings', to: '/meetings/assign', permission: { module: 'Meetings', section: 'Distribution', action: 'manual_assign' } },
      { label: 'My Meetings', to: '/meetings/my-meetings', permission: { module: 'Meetings', section: 'Distribution', action: 'view' } },
      { label: 'Calendar', to: '/meetings/calendar', permission: { module: 'Meetings', section: 'Calendar', action: 'view' } },
      { label: 'Permissions', to: '/meetings/permissions', permission: { module: 'Meetings', section: 'AgentSettings', action: 'manage_credentials' } },
    ],
  },
  {
    label: 'Reports',
    to: '/reports',
    icon: FileTextIcon,
    permission: null,
    submenu: [
      {
        label: 'Investor Dashboard',
        to: '/investor-dashboard',
        permission: { module: 'InvestorDashboard', section: 'Main', action: 'view' },
      },
      {
        label: 'Success Criteria',
        to: '/success-criteria',
        permission: { module: 'SuccessCriteria', section: 'CriteriaList', action: 'view' },
      },
      {
        label: 'Tickets Analytics',
        to: '/tickets/analytics',
        permission: { module: 'Tickets', section: 'Main', action: 'view' },
      },
      {
        label: 'Users Sales Summary',
        to: '/userSalesSummary',
        permission: { module: 'UserManagement', section: 'UserSalesSummary', action: 'view' },
      },
      {
        label: 'Sales Summary v2',
        to: '/sales-summary-v2',
        permission: { module: 'UserManagement', section: 'UserSalesSummary', action: 'view' },
      },
      {
        label: 'Accounts Analysis',
        to: '/reports/accounts-analysis',
        permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' },
      },
      {
        label: 'Owner Dashboard',
        to: '/reports/owner-dashboard',
        permission: { module: 'OwnerDashboard', section: 'Main', action: 'view' },
      },
      {
        label: 'Payment Analytics',
        to: '/payment-analytics',
        permission: { module: 'Reports', section: 'PaymentAnalytics', action: 'view' },
      },
      {
        label: 'Payment Analytics (Legacy)',
        to: '/payment-analytics-legacy',
        permission: { module: 'Reports', section: 'PaymentAnalytics', action: 'view' },
      },
      {
        label: 'WhatsApp Analytics',
        to: '/whatsapp/analytics',
        permission: { module: 'WhatsAppAnalytics', section: 'Main', action: 'view' },
      },
      // Attendance report is now built into the Attendance page tabs
      {
        label: 'Superadmin Dashboard',
        to: '/superadmin-dashboard',
        permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' },
      },
      {
        label: 'Comparison Graphs',
        to: '/comparison-graphs',
        permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' },
      },
      {
        label: 'Support Conversations',
        to: '/reports/support-conversations',
        permission: { module: 'WhatsApp', section: 'Main', action: 'manage' },
      },
      {
        label: 'Support Report',
        to: '/reports/support-report',
        permission: { module: 'SuccessCriteria', section: 'SupportReport', action: 'view' },
      },
      {
        label: 'Agent Performance',
        to: '/reports/agent-performance',
        permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' },
      },
      {
        label: 'CRM Analytics',
        permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' },
        submenu: [
          { label: 'Platform Usage', to: '/crm-analytics/usage', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
          { label: 'Lead Management', to: '/crm-analytics/leads', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
          { label: 'Communication', to: '/crm-analytics/communication', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
          { label: 'Properties', to: '/crm-analytics/properties', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
          { label: 'Subscriptions', to: '/crm-analytics/subscriptions', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
          { label: 'Search Behavior', to: '/crm-analytics/search', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
          { label: 'Teams', to: '/crm-analytics/teams', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
          { label: 'Growth & Signups', to: '/crm-analytics/growth', permission: { module: 'SuperadminDashboard', section: 'Main', action: 'view' } },
        ],
      },
    ],
  },
];

const QUICK_ACCESS_SIDEBAR_ITEMS = [
  {
    label: 'Dashboard',
    to: '/dashboard',
    styleKey: 'Dashboard',
    icon: GlobeIcon,
    permission: { module: 'Dashboard', section: 'Main', action: 'view' },
  },
  {
    label: 'Calendar',
    to: '/calendar',
    styleKey: 'Calendar',
    icon: CalendarIcon,
    permission: { module: 'Calendar', section: 'Main', action: 'view' },
  },
  {
    label: 'WhatsApp Support',
    to: '/whatsapp-support',
    styleKey: 'WhatsApp',
    icon: WhatsAppIcon,
    permission: { module: 'WhatsApp', section: 'Main', action: 'view' },
  },
  {
    label: 'Mailbox',
    to: '/email',
    styleKey: 'Mailbox',
    icon: InboxIcon,
    permission: { module: 'Email', section: 'Main', action: 'view' },
  },
  {
    label: 'Tasks',
    to: '/tasks',
    styleKey: 'Tasks',
    icon: ListTodoIcon,
    permission: { module: 'Tasks', section: 'Main', action: 'view' },
  },
];

// Right side menu items (icons)
const RIGHT_MENU_ITEMS = [
  {
    label: 'Calendar',
    to: '/calendar',
    icon: CalendarIcon,
    permission: { module: 'Calendar', section: 'Main', action: 'view' },
  },
  {
    label: 'Mailbox',
    to: '/email',
    icon: InboxIcon,
    permission: { module: 'Email', section: 'Main', action: 'view' },
  },
  {
    label: 'WhatsApp',
    to: '/whatsapp',
    icon: WhatsAppIcon,
    permission: { module: 'WhatsApp', section: 'Main', action: 'view' },
  },
  {
    label: 'Activity',
    to: '/activity',
    icon: HistoryIcon,
    permission: { module: 'Activity', section: 'Main', action: 'view' },
  },
];

// Add Admin Controls menu config after MENU_ITEMS
const ADMIN_MENU_ITEMS = [
  {
    label: 'User Management',
    to: '/userManagement',
    permission: { module: 'UserManagement', section: 'Main', action: 'view' },
  },
  {
    label: 'Commission Management',
    to: '/commissionManagement',
    permission: { module: 'CommissionManagement', section: 'Main', action: 'view' },
  },
  {
    label: 'Role Management',
    to: '/roleManagement',
    permission: { module: 'RoleManagement', section: 'Main', action: 'view' },
  },
  {
    label: 'Contract Templates',
    to: '/contracts/admin',
    permission: { module: 'Contracts', section: 'ContractTemplateAdmin', action: 'view' },
  },
  {
    label: 'AI Settings',
    to: '/settings/ai',
    permission: { module: 'Superadmin', section: 'AISettings', action: 'view' },
  },
  {
    label: 'RAG Service',
    to: '/settings/rag',
    permission: { module: 'Superadmin', section: 'AISettings', action: 'view' },
  },
  // OLD: Attendance Settings now built into AttendanceV2Page tabs
  {
    label: 'Meeting Agent Settings',
    to: '/meetings/agents',
    permission: { module: 'Meetings', section: 'AgentSettings', action: 'view' },
  },
  {
    label: 'Support Automation',
    permission: { module: 'WhatsApp', section: 'Main', action: 'manage' },
    submenu: [
      { label: 'Flow Builder', to: '/support-automation/flows', permission: { module: 'WhatsApp', section: 'Main', action: 'manage' } },
      { label: 'Agent Availability', to: '/support-automation/agents', permission: { module: 'WhatsApp', section: 'Main', action: 'manage' } },
      { label: 'Client Configuration', to: '/support-automation/clients', permission: { module: 'WhatsApp', section: 'Main', action: 'manage' } },
      { label: 'Automation Settings', to: '/support-automation/settings', permission: { module: 'WhatsApp', section: 'Main', action: 'manage' } },
      { label: 'Statistics', to: '/support-automation/stats', permission: { module: 'WhatsApp', section: 'Main', action: 'manage' } },
    ],
  },
  {
    label: 'Company Settings',
    to: '/settings/company',
    permission: { module: 'Superadmin', section: 'CompanySettings', action: 'view' },
  },
  {
    label: 'S3 Browser',
    to: '/settings/s3-browser',
    permission: { module: 'Superadmin', section: 'S3Browser', action: 'view' },
  },
  {
    label: 'Locations Fetcher',
    to: '/settings/locations-fetcher',
    permission: { module: 'Superadmin', section: 'LocationsFetcher', action: 'view' },
  },
  {
    label: 'System Health',
    to: '/settings/system-health',
    permission: { module: 'Superadmin', section: 'SystemHealth', action: 'view' },
  },
  {
    label: 'VPN',
    to: '/settings/vpn',
    permission: { module: 'Superadmin', section: 'VPN', action: 'view' },
  },
  {
    label: 'Database Backup',
    to: '/database-backup',
    permission: { module: 'DatabaseBackup', section: 'Main', action: 'view' },
  },
  {
    label: 'Phone Parser Tool',
    to: '/settings/phone-parser',
    permission: { module: 'Superadmin', section: 'PhoneParser', action: 'view' },
  },
];

function hasPermission(userPermissions: string[], required: { module: string; section: string; action: string } | null, userRoles: string[], permissionsLoaded: boolean, meetingPermissions?: any): boolean {
  if (userRoles && userRoles.includes('Superadmin')) return true;
  if (!required) return true;
  if (!permissionsLoaded) return false; // Don't allow access until permissions are loaded

  // Check exact permission, including HR recruitment permissions that grant the same screen
  if (hasListedPermission(userPermissions, required.module, required.section, required.action)) return true;

  // For Meetings module, also check MeetingPermission model
  if (required.module === 'Meetings') {
    if (meetingPermissions) {
      // Map MeetingPermission fields to permission checks
      if (required.section === 'Distribution' && required.action === 'create') {
        // Can create meetings
        if (meetingPermissions.canCreateMeetings) return true;
      }
      if (required.section === 'Distribution' && required.action === 'view') {
        // Can view meetings (for menu visibility)
        if (meetingPermissions.canViewMeetings) return true;
      }
      if (required.section === 'Calendar' && required.action === 'view') {
        // Can view calendar
        if (meetingPermissions.canViewMeetings || meetingPermissions.canViewAllMeetings) return true;
      }
      if (required.section === 'Calendar' && required.action === 'view_all') {
        // Can view all meetings
        if (meetingPermissions.canViewAllMeetings) return true;
      }
      if (required.section === 'Calendar' && required.action === 'reassign') {
        // Can reassign meetings
        if (meetingPermissions.canReassignMeetings) return true;
      }
      if (required.section === 'AgentSettings' && required.action === 'manage_credentials') {
        // Can manage agent settings/permissions
        if (meetingPermissions.canManageAgentSettings) return true;
      }
    }
    // If no meeting permissions exist but user is a sales agent, check if they have role-based permission
    // This is a fallback for agents who haven't been added to meeting config yet
    // The role-based permission check already happened above, so we just return false here
  }

  // For Accounting Reports: if specific report permission is required, also check general 'view' permission
  if (required.module === 'Accounting' && required.section === 'Reports') {
    if (required.action !== 'view' && required.action !== 'export') {
      // Check for general view permission
      const generalViewPerm = `${required.module}:${required.section}:view`;
      if (userPermissions.includes(generalViewPerm)) return true;
    }
  }

  // Recruitment settings: manage actions also open the settings page
  if (required.module === 'Recruitment' && required.section === 'Settings' && required.action === 'view') {
    if (userPermissions.includes('Recruitment:Settings:manage_skills')) return true;
    if (userPermissions.includes('Recruitment:Settings:manage_stages')) return true;
    if (userPermissions.includes('Recruitment:Settings:manage_fit_labels')) return true;
  }

  // For Accounting Income/Expenses: if menu checks for 'record', also allow 'view' permission
  if (required.module === 'Accounting' && (required.section === 'Income' || required.section === 'Expenses')) {
    if (required.action === 'record') {
      const viewPermString = `${required.module}:${required.section}:view`;
      if (userPermissions.includes(viewPermString)) return true;
    }
    if (required.action === 'view') {
      const recordPermString = `${required.module}:${required.section}:record`;
      if (userPermissions.includes(recordPermString)) return true;
    }
  }

  return false;
}

function hasAnySubmenuPermission(userPermissions: string[], submenu: any[], userRoles: string[], permissionsLoaded: boolean, meetingPermissions?: any): boolean {
  if (userRoles && userRoles.includes('Superadmin')) return true;
  if (!permissionsLoaded) return false;
  return submenu.some(sub => {
    // Check if current item has permission
    if (hasPermission(userPermissions, sub.permission, userRoles, permissionsLoaded, meetingPermissions)) return true;
    // Recursively check nested submenus (for 3-level menus)
    if (sub.submenu && hasAnySubmenuPermission(userPermissions, sub.submenu, userRoles, permissionsLoaded, meetingPermissions)) return true;
    return false;
  });
}

function mapBackendRoleToUserRole(role: string): UserRole {
  switch (role) {
    case 'Sales Agent': return UserRole.SalesAgent;
    case 'Operator': return UserRole.Operator;
    case 'Manager': return UserRole.Manager;
    case 'Quality Assurance': return UserRole.QualityAssurance;
    case 'Admin': return UserRole.Admin;
    case 'Web Manager': return UserRole.WebManager;
    case 'Superadmin': return UserRole.Superadmin;
    default: return UserRole.Manager; // fallback
  }
}

const App: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUserRoles, setCurrentUserRoles] = useState<string[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  /** checking | required | ok — gate users without a passkey after login */
  const [passkeyGate, setPasskeyGate] = useState<'checking' | 'required' | 'ok'>('checking');
  const [currentUsername, setCurrentUsername] = useState<string | null>(null);
  const [currentUserName, setCurrentUserName] = useState<string | null>(null);
  const [currentUserProfilePic, setCurrentUserProfilePic] = useState<string | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [contractsMenuOpen, setContractsMenuOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [remVoiceAgentId, setRemVoiceAgentId] = useState<string | null>(null);
  const [clientsMenuOpen, setClientsMenuOpen] = useState(false);
  const clientsMenuRef = useRef<HTMLDivElement>(null);

  const [ticketsMenuOpen, setTicketsMenuOpen] = useState(false);
  const ticketsMenuRef = useRef<HTMLDivElement>(null);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);
  const reportsMenuRef = useRef<HTMLDivElement>(null);
  const [accountingMenuOpen, setAccountingMenuOpen] = useState(false);
  const accountingMenuRef = useRef<HTMLDivElement>(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const adminMenuRef = useRef<HTMLDivElement>(null);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [meetingPermissions, setMeetingPermissions] = useState<any>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [isSalesAgent, setIsSalesAgent] = useState<boolean>(false);
  const [hasDevTrackerAccess, setHasDevTrackerAccess] = useState(false);
  const [frequentMenuVersion, setFrequentMenuVersion] = useState(0);
  const [pinnedMenuVersion, setPinnedMenuVersion] = useState(0);
  const [clientSaveError, setClientSaveError] = useState<string | null>(null);
  const [whatsappUnreadCount, setWhatsappUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [mobileSubMenus, setMobileSubMenus] = useState<{ [key: string]: boolean }>({
    'Clients': false,
    'Tickets': false,
    'Accounting': false,
    'Reports': false,
    'Settings': false,
    'Mailbox': false,
  });
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [isAIChatMinimized, setIsAIChatMinimized] = useState(false);
  const [leftNavSubMenus, setLeftNavSubMenus] = useState<{ [key: string]: boolean }>(() => ({
    'Clients': false,
    'Accounting': false,
    'Accounting.Settings': false,
    'Accounting.Reports': false,
    'Reports': false,
    'Tickets': false,
    'Settings': false,
    'Settings.Support Automation': false,
    'Mailbox': false,
  }));
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  /** Remember expand/collapse before entering Mailbox so we can restore on leave */
  const sidebarBeforeMailboxRef = useRef<boolean | null>(null);
  const [clickedMenuItem, setClickedMenuItem] = useState<string | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return true;
    return window.matchMedia('(min-width: 1024px)').matches;
  });

  // Handle submenu positioning on click
  const handleMenuClick = (event: React.MouseEvent, menuLabel: string) => {
    if (isSidebarCollapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setSubmenuPosition({
        top: rect.top,
        left: rect.right + 8 // 8px gap from the sidebar
      });
      setClickedMenuItem(clickedMenuItem === menuLabel ? null : menuLabel);
    } else {
      setLeftNavSubMenus(prev => ({ ...prev, [menuLabel]: !prev[menuLabel] }));
    }
  };

  // Handle user info popover positioning
  const handleUserInfoClick = (event: React.MouseEvent) => {
    if (isSidebarCollapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setSubmenuPosition({
        top: rect.top,
        left: rect.right + 8 // 8px gap from the sidebar
      });
      setClickedMenuItem(clickedMenuItem === 'UserInfo' ? null : 'UserInfo');
    }
  };

  // Handle navigation with support for opening in new tab
  const handleNavigation = (event: React.MouseEvent, to: string) => {
    // Check for right-click or cmd/ctrl + click
    if (event.button === 2 || event.metaKey || event.ctrlKey) {
      // Open in new tab
      window.open(to, '_blank');
    } else if (event.button === 0) {
      // Normal left click - navigate in same tab
      navigate(to);
    }
  };

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSidebarCollapsed && clickedMenuItem) {
        const target = event.target as Element;
        if (!target.closest('.submenu-container') && !target.closest('.menu-button')) {
          setClickedMenuItem(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSidebarCollapsed, clickedMenuItem]);

  // Permission checks
  const canViewClientList = usePermission('Clients', 'ClientList', 'view');
  const canEditClientList = usePermission('Clients', 'ClientList', 'edit');
  const canViewWebClients = usePermission('Clients', 'WebClients', 'view');
  const canViewContractGenerator = usePermission('Contracts', 'ContractGenerator', 'view');
  const canViewSavedContracts = usePermission('Contracts', 'SavedContracts', 'view');
  const canViewContractTemplates = usePermission('Contracts', 'ContractTemplateAdmin', 'view');
  const canViewCalendar = usePermission('Calendar', 'Main', 'view');
  const canViewEmail = usePermission('Email', 'Main', 'view');
  const canViewWhatsApp = usePermission('WhatsApp', 'Main', 'view');
  const canViewActivity = usePermission('Activity', 'Main', 'view');
  const canViewSuccessCriteria = usePermission('SuccessCriteria', 'CriteriaList', 'view');
  const canViewUserManagement = usePermission('UserManagement', 'Main', 'view');
  const canViewUserSalesSummary = usePermission('UserManagement', 'UserSalesSummary', 'view');
  const canViewCommissionManagement = usePermission('CommissionManagement', 'Main', 'view');
  const canViewRoleManagement = usePermission('RoleManagement', 'Main', 'view');
  const canViewSuperadminDashboard = hasPermission(
    userPermissions,
    { module: 'SuperadminDashboard', section: 'Main', action: 'view' },
    currentUserRoles,
    permissionsLoaded
  );
  const canViewOwnerDashboard = hasPermission(
    userPermissions,
    { module: 'OwnerDashboard', section: 'Main', action: 'view' },
    currentUserRoles,
    permissionsLoaded
  );
  const canViewInvestorDashboard = hasPermission(
    userPermissions,
    { module: 'InvestorDashboard', section: 'Main', action: 'view' },
    currentUserRoles,
    permissionsLoaded
  );
  const canViewDashboard = hasPermission(
    userPermissions,
    { module: 'Dashboard', section: 'Main', action: 'view' },
    currentUserRoles,
    permissionsLoaded
  );

  // Determine default route based on permission priority
  const getDefaultRoute = () => {
    if (canViewSuperadminDashboard) return '/superadmin-dashboard';
    if (canViewInvestorDashboard) return '/investor-dashboard';
    if (canViewDashboard) return '/dashboard';
    return '/profile';
  };

  // MOCK: In a real app, this would be the logged-in user's ID.
  const MOCK_SALES_AGENT_ID = 'sales-agent-1';

  const [remarkModalState, setRemarkModalState] = useState<RemarkModalState>({
    isOpen: false,
    taskKey: null,
    clientId: null,
    newStatus: null,
    action: null
  });

  const logActivity = useCallback((description: string, clientId: string) => {
    const newLog: ActivityLog = {
      id: `log-${new Date().toISOString()}-${Math.random()}`,
      description,
      clientId,
      authorRole: currentUserRoles[0],
      timestamp: new Date().toISOString()
    };
    setActivityLogs((prev: ActivityLog[]) => [newLog, ...prev]);
  }, [currentUserRoles]);

  const handleOpenModal = (client: Client | null, clearError: boolean = true) => {
    if (clearError) {
      setClientSaveError(null); // Only clear error when explicitly requested
    }
    if (client && !client.id && (client as any)._id) {
      client.id = (client as any)._id;
    }
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedClient(null);
    setIsModalOpen(false);
    setClientSaveError(null); // Clear error when closing modal
  };

  // Fetch clients and users on mount and after mutations
  const fetchClients = useCallback(() => {
    fetch('/api/clients', { credentials: 'include' })
      .then(res => res.json())
      .then(data => setClients(data));
  }, []);
  const fetchUsers = useCallback(() => {
    // Always provide the Contract Generator with the full list of sales agents (no pagination)
    const endpoint = '/api/users/sales-agents?getAllUsers=true';

    fetch(endpoint, { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        // Extract users array from paginated response
        const usersArray = data.users || data;
        setUsers(Array.isArray(usersArray) ? usersArray : []);
      })
      .catch(error => {
        console.error('Error fetching users:', error);
        setUsers([]);
      });
  }, [currentUserRoles]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchClients();
      fetchUsers();
    }
  }, [isLoggedIn, fetchClients, fetchUsers]);

  // Update handleSaveClient to refetch and redirect
  const handleSaveClient = async (clientToSave: Client) => {
    // Do not clear error here, only on open/close
    let savedClient: any;
    if (clientToSave.id) {
      // Edit existing client
      const { _id, id, ...rest } = clientToSave as any;
      const res = await fetch(`/api/clients/${clientToSave.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(rest)
      });
      if (!res.ok) {
        const errorData = await res.json();
        setClientSaveError(errorData.error || 'Failed to update client');
        console.log('Set clientSaveError:', errorData.error || 'Failed to update client');
        // Do NOT close modal or reset selectedClient here
        return;
      }
      savedClient = await res.json();
      fetchClients();
      setIsModalOpen(false); // just close the modal
      setSelectedClient(null); // reset selectedClient on success
    } else {
      // Add new client
      const { _id, id, ...rest } = clientToSave as any;
      if (currentUserRoles.includes('Sales Agent') && currentUsername) {
        rest.salesAgentId = currentUsername;
      }
      const res = await fetch('/api/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(rest)
      });
      if (!res.ok) {
        const errorData = await res.json();
        setClientSaveError(errorData.error || 'Failed to create client');
        console.log('Set clientSaveError:', errorData.error || 'Failed to create client');
        // Do NOT close modal or reset selectedClient here
        return;
      }
      savedClient = await res.json();
      fetchClients();
      setIsModalOpen(false); // just close the modal
      setSelectedClient(null); // reset selectedClient on success
      navigate('/clients'); // go to list
    }
  };

  const handleAddNewClient = () => {
    const now = new Date().toISOString();
    const newClientTemplate: Client = {
      id: '',
      name: '',
      company: '',
      companyId: 'secondary',
      email: '',
      numberOfUsers: 10,
      status: ClientStatus.Onboarding,
      onboardingTasks: {
        hasWebsite: false,
        websiteDelivered: { status: TaskStatus.Pending, remarks: [], scheduledDate: null },
        hasSalesFunnel: false,
        salesFunnelDelivered: { status: TaskStatus.Pending, remarks: [], scheduledDate: null },
        adminTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null },
        agentTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null },
        websiteTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null },
      },
      qualityCheckRemarks: '',
      clientFeedback: '',
      satisfactionLevel: null,
      renewalPrediction: null,
      createdAt: now,
      modifiedAt: now,
      totalPrice: 0,
      isPaymentPending: false,
      pendingAmount: 0,
      paymentDueDate: null,
      attachments: [],
      paymentType: 'Yearly',
      firstPayment: undefined,
      monthlyPayment: undefined,
    };
    handleOpenModal(newClientTemplate);
  };

  const handleViewClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const handleTaskStatusUpdateRequest = (
    clientId: string,
    taskKey: keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'>,
    newStatus: TaskStatus,
    newDate?: string
  ) => {
    let action: RemarkAction = 'Note';
    if (newStatus === TaskStatus.Approved) action = 'Approved';
    if (newStatus === TaskStatus.Pending) action = 'Declined';
    if (newStatus === TaskStatus.Reported) action = 'Reported';
    if (newStatus === TaskStatus.Rescheduled) action = 'Rescheduled';

    if (newStatus === TaskStatus.Rescheduled && newDate) {
      // 1. Push current scheduledDate/status to history as Rescheduled
      // 2. Set current task to new date, Pending, clear remarks
      handleConfirmTaskStatusUpdate(clientId, taskKey, TaskStatus.Rescheduled, action, `Task rescheduled to ${new Date(newDate).toLocaleString()} by ${currentUserRoles[0]}.`, newDate, true);
      return;
    }

    if (action === 'Approved' || action === 'Declined') {
      setRemarkModalState({ isOpen: true, clientId, taskKey, newStatus, action });
    } else {
      // For 'Reported', 'Rescheduled', or other status changes, add remark automatically without modal
      let remark = `Task status set to ${newStatus} by ${currentUserRoles[0]}.`;
      if (newStatus === TaskStatus.Rescheduled && newDate) {
        remark = `Task rescheduled to ${new Date(newDate).toLocaleString()} by ${currentUserRoles[0]}.`;
      }
      handleConfirmTaskStatusUpdate(clientId, taskKey, newStatus, action, remark, newDate);
    }
  };

  const handleConfirmTaskStatusUpdate = async (
    clientId: string,
    taskKey: keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'>,
    newStatus: TaskStatus,
    action: RemarkAction,
    remarkText: string,
    newDate?: string,
    isReschedule?: boolean
  ) => {
    // Store remark in backend if Approved or Declined
    if (action === 'Approved' || action === 'Declined') {
      await fetch(`/api/clients/${clientId}/remarks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ taskKey, action, remark: remarkText })
      });
    }
    // Update onboardingTasks in backend
    const client = clients.find(c => c.id === clientId);
    if (client) {
      // Only copy keys that are OnboardingTask, not booleans
      const onboardingTaskKeys = Object.keys(client.onboardingTasks).filter(k => typeof (client.onboardingTasks as any)[k] === 'object');
      const updatedTasks: { [key: string]: OnboardingTask } = {};
      onboardingTaskKeys.forEach(k => { updatedTasks[k] = (client.onboardingTasks as any)[k]; });
      const taskToUpdate = updatedTasks[taskKey];
      if (!taskToUpdate || typeof taskToUpdate !== 'object' || !('status' in taskToUpdate)) return; // Type guard
      const newRemark = {
        id: new Date().toISOString(),
        text: remarkText,
        authorRole: currentUserRoles[0],
        timestamp: new Date().toISOString(),
        action: action
      };
      let updatedTask = { ...taskToUpdate };
      if (isReschedule && newDate) {
        const safeTask = taskToUpdate as OnboardingTask;
        updatedTask.history = [
          ...((safeTask.history as { scheduledDate: string | null; status: TaskStatus }[]) || []),
          { scheduledDate: safeTask.scheduledDate, status: TaskStatus.Rescheduled }
        ];
        // Set current to new date, Pending, clear remarks
        updatedTask.status = TaskStatus.Pending;
        updatedTask.scheduledDate = newDate;
        updatedTask.remarks = [];
      } else if (newStatus === TaskStatus.Pending && newDate) {
        updatedTask = {
          ...taskToUpdate,
          status: TaskStatus.Pending,
          scheduledDate: newDate,
          remarks: [],
        };
      } else if (newStatus === TaskStatus.Rescheduled && newDate) {
        const safeTask = taskToUpdate as OnboardingTask;
        updatedTask.history = [
          ...((safeTask.history as { scheduledDate: string | null; status: TaskStatus }[]) || []),
          { scheduledDate: safeTask.scheduledDate, status: safeTask.status }
        ];
        updatedTask.status = TaskStatus.Rescheduled;
        updatedTask.remarks = [...safeTask.remarks, newRemark];
        updatedTask.scheduledDate = newDate;
      } else {
        updatedTask = {
          ...taskToUpdate,
          status: newStatus,
          remarks: [...taskToUpdate.remarks, newRemark],
          scheduledDate: newStatus === TaskStatus.Rescheduled && newDate ? newDate : taskToUpdate.scheduledDate
        };
      }
      if (taskToUpdate && typeof taskToUpdate === 'object' && 'status' in taskToUpdate) {
        const safeTask = taskToUpdate as OnboardingTask;
        updatedTask.history = [
          ...((safeTask.history as { scheduledDate: string | null; status: TaskStatus }[]) || []),
          { scheduledDate: safeTask.scheduledDate, status: safeTask.status }
        ];
      }
      updatedTasks[taskKey] = updatedTask as OnboardingTask;
      const updatedClient = { ...client, onboardingTasks: updatedTasks, modifiedAt: new Date().toISOString() };
      const { _id, id, ...rest } = updatedClient as any;
      // Debug: log the payload being sent
      console.log('Updating client:', rest);
      const res = await fetch(`/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(rest)
      });
      const saved = await res.json();
      saved.id = saved._id;
      setClients(prev => prev.map(c => c.id === saved._id ? saved : c));
    }
    setRemarkModalState({ isOpen: false, taskKey: null, clientId: null, newStatus: null, action: null });
    setIsModalOpen(false); // Close the modal after status change
  };

  const pendingApprovalCount = useMemo(() => {
    return clients.reduce((count: number, client: Client) => {
      const tasks = client.onboardingTasks;
      return count + Object.keys(tasks)
        .filter(key => key !== 'hasWebsite' && key !== 'hasSalesFunnel')
        .reduce((taskCount: number, taskKey: string) => {
          const key = taskKey as keyof Omit<OnboardingTasks, 'hasWebsite' | 'hasSalesFunnel'>;
          const task = tasks[key];
          if (typeof task === 'object' && task !== null && 'status' in task && (task as OnboardingTask).status === TaskStatus.Reported) {
            return taskCount + 1;
          }
          return taskCount;
        }, 0);
    }, 0);
  }, [clients]);

  const NavLink: React.FC<{
    to: string;
    children: React.ReactNode;
    badgeCount?: number;
    hidden?: boolean;
  }> = ({ to, children, badgeCount = 0, hidden }) => {
    if (hidden) return null;
    return (
      <Link
        to={to}
        className="relative px-3 py-2 text-sm font-medium rounded-md transition-colors text-slate-500 hover:bg-slate-200 hover:text-slate-700"
      >
        {children}
        {badgeCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
            {badgeCount}
          </span>
        )}
      </Link>
    );
  };

  const visibleClients = useMemo(() =>
    isSalesAgent && currentUsername
      ? clients.filter((c: Client) => c.salesAgentId === currentUsername)
      : clients,
    [clients, isSalesAgent, currentUsername]);

  const handleLoginSuccess = async (user: { username: string; roles: string[]; id?: string; _id?: string; isSalesAgent?: boolean }) => {
    const uid = user.id || user._id || null;
    // Force passkey setup prompt on every fresh login (clear any "skip for now")
    if (uid) {
      try {
        sessionStorage.removeItem(`passkey_setup_skipped_${uid}`);
      } catch {
        /* ignore */
      }
    }
    setPasskeyGate('checking');
    setCurrentUserRoles(user.roles || []);
    setCurrentUsername(user.username);
    setCurrentUserId(uid);
    setIsLoggedIn(true);
    setIsSalesAgent(user.isSalesAgent || false);
    setRemVoiceAgentId(null);

    // Check dev tracker access for menu visibility
    fetch('/api/dev-tracker/access-check', { credentials: 'include' })
      .then(r => r.ok ? r.json() : { hasAccess: false })
      .then(d => setHasDevTrackerAccess(d.hasAccess))
      .catch(() => {});

    // Clear previous user's profile data immediately
    setCurrentUserName(null);
    setCurrentUserProfilePic(null);

    // Fetch the new user's profile data
    try {
      const profileRes = await fetch('/api/auth/me', { credentials: 'include' });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setCurrentUserName(profileData.name);
        setCurrentUserProfilePic(profileData.profilePictureUrl);
        setRemVoiceAgentId(profileData.remVoiceAgentId || null);
      }
    } catch (err) {
      console.error('Error fetching profile data:', err);
    }
  };

  // Check session on mount
  useEffect(() => {
    fetch('/api/auth/session', {
      credentials: 'include',
    })
      .then(res => res.json())
      .then(async (data) => {
        if (data.loggedIn) {
          setCurrentUserRoles(data.roles || []);
          setCurrentUsername(data.username);
          setCurrentUserId(data.id || data._id || null);
          setIsLoggedIn(true);
          setIsSalesAgent(data.isSalesAgent || false);
          setRemVoiceAgentId(data.remVoiceAgentId || null);

          // Fetch full profile to get profile picture
          try {
            const profileRes = await fetch('/api/auth/me', { credentials: 'include' });
            if (profileRes.ok) {
              const profileData = await profileRes.json();
              setCurrentUserName(profileData.name);
              setCurrentUserProfilePic(profileData.profilePictureUrl);
              setRemVoiceAgentId(profileData.remVoiceAgentId || null);
            }
          } catch (err) {
            console.error('Error fetching profile data:', err);
          }

          // Check dev tracker access for menu visibility
          try {
            const dtRes = await fetch('/api/dev-tracker/access-check', { credentials: 'include' });
            if (dtRes.ok) {
              const dtData = await dtRes.json();
              setHasDevTrackerAccess(dtData.hasAccess);
            }
          } catch (_) {}
        }
        setSessionChecked(true);
      });
  }, []);

  // Periodic session heartbeat to detect suspension during active usage
  useEffect(() => {
    if (!isLoggedIn) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/auth/session', { credentials: 'include' });
        const data = await res.json();
        if (!data.loggedIn) {
          setIsLoggedIn(false);
          setCurrentUserRoles([]);
          setCurrentUsername(null);
          setCurrentUserId(null);
          setCurrentUserName(null);
          setCurrentUserProfilePic(null);
          if (data.suspended) {
            alert('Your account has been suspended. Please contact your administrator.');
          }
        }
      } catch (err) {
        // Network error, skip this check
      }
    }, 60000); // Check every 60 seconds
    return () => clearInterval(interval);
  }, [isLoggedIn]);

  // Fetch activity logs from API when logged in
  useEffect(() => {
    if (isLoggedIn) {
      fetch('/api/clients/activities', { credentials: 'include' })
        .then(res => res.json())
        .then(data => setActivityLogs(data));
    }
  }, [isLoggedIn]);

  // Fetch user permissions on login/session
  useEffect(() => {
    if (isLoggedIn) {
      Promise.all([
        fetch('/api/auth/permissions', { credentials: 'include' }).then(res => res.json()),
        fetch('/api/meetings/permissions/me', { credentials: 'include' })
          .then(res => {
            if (res.ok) {
              return res.json();
            }
            return null;
          })
          .catch(() => null)
      ]).then(([perms, meetingPerms]) => {
        // Map to string triplets for usePermission
        const permStrings = (perms || []).map((p: any) => {
          const action = p.action || 'view';
          return `${p.module}:${p.section}:${action}`;
        });
        setUserPermissions(permStrings);
        setMeetingPermissions(meetingPerms);
        console.log('[App] Meeting permissions loaded:', meetingPerms);
        console.log('[App] User permissions:', permStrings);
        setPermissionsLoaded(true);
      }).catch(err => {
        console.error('Error fetching permissions:', err);
        setPermissionsLoaded(true); // Still set loaded to true to prevent blocking
      });
    } else {
      setPermissionsLoaded(false);
      setMeetingPermissions(null);
    }
  }, [isLoggedIn]);

  // Close user menu on outside click
  useEffect(() => {
    if (!isUserMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isUserMenuOpen]);

  // Close Clients submenu on outside click
  useEffect(() => {
    if (!clientsMenuOpen) return;
    function handleClick(e: MouseEvent) {
      if (clientsMenuRef.current && !clientsMenuRef.current.contains(e.target as Node)) {
        setClientsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [clientsMenuOpen]);



  // Close Tickets menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ticketsMenuRef.current && !ticketsMenuRef.current.contains(e.target as Node)) {
        setTicketsMenuOpen(false);
      }
    }
    if (ticketsMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [ticketsMenuOpen]);

  // Close Admin menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (adminMenuRef.current && !adminMenuRef.current.contains(e.target as Node)) {
        setAdminMenuOpen(false);
      }
    }
    if (adminMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [adminMenuOpen]);

  // Navigation helpers
  const navigate = useNavigate();
  const location = useLocation() as Location & { pathname: string };

  const menuRegistry = useMemo(() => {
    const flatEntries = [
      ...flattenMenuNavigation(QUICK_ACCESS_SIDEBAR_ITEMS),
      ...flattenMenuNavigation(MENU_ITEMS),
      ...flattenMenuNavigation(ADMIN_MENU_ITEMS, { styleKey: 'Settings' }),
      ...flattenMenuNavigation(RIGHT_MENU_ITEMS),
    ];
    return buildMenuRegistry(flatEntries);
  }, []);

  const menuIconByStyleKey = useMemo(() => {
    const map = new Map<string, React.ComponentType<{ className?: string }>>();
    QUICK_ACCESS_SIDEBAR_ITEMS.forEach((item) => map.set(item.styleKey, item.icon));
    MENU_ITEMS.forEach((item) => {
      if (item.icon) map.set(item.label, item.icon);
    });
    map.set('Settings', CogIcon);
    map.set('Investor Dashboard', ChartBarIcon);
    map.set('Success Criteria', TrendingUpIcon);
    return map;
  }, []);

  const canAccessMenuNavEntry = useCallback(
    (entry: MenuNavEntry) => {
      if (entry.path === '/meetings/permissions') {
        return currentUserRoles.includes('Superadmin');
      }
      if (entry.permission?.section === 'DevTracker') {
        if (entry.label === 'Settings' || entry.label === 'Releases') {
          return hasPermission(
            userPermissions,
            entry.permission,
            currentUserRoles,
            permissionsLoaded,
            meetingPermissions
          );
        }
        return (
          hasDevTrackerAccess ||
          hasPermission(
            userPermissions,
            entry.permission,
            currentUserRoles,
            permissionsLoaded,
            meetingPermissions
          )
        );
      }
      if (!entry.permission) return true;
      return hasPermission(
        userPermissions,
        entry.permission,
        currentUserRoles,
        permissionsLoaded,
        meetingPermissions
      );
    },
    [
      currentUserRoles,
      hasDevTrackerAccess,
      meetingPermissions,
      permissionsLoaded,
      userPermissions,
    ]
  );

  const quickAccessPaths = useMemo(
    () => new Set(QUICK_ACCESS_SIDEBAR_ITEMS.map((item) => normalizeMenuPath(item.to))),
    []
  );

  const pinnedMenuEntries = useMemo(() => {
    if (!currentUserId) return [];
    void pinnedMenuVersion;
    return resolvePinnedMenuEntries(
      currentUserId,
      menuRegistry,
      canAccessMenuNavEntry
    );
  }, [
    canAccessMenuNavEntry,
    currentUserId,
    menuRegistry,
    pinnedMenuVersion,
  ]);

  const pinnedPathSet = useMemo(
    () => new Set(pinnedMenuEntries.map((entry) => entry.path)),
    [pinnedMenuEntries]
  );

  const mostUsedMenuEntries = useMemo(() => {
    if (!currentUserId) return [];
    void frequentMenuVersion;
    const excludePaths = new Set([...pinnedPathSet, ...quickAccessPaths]);
    return resolveMostUsedMenuEntries(
      currentUserId,
      menuRegistry,
      canAccessMenuNavEntry,
      undefined,
      excludePaths
    );
  }, [
    canAccessMenuNavEntry,
    currentUserId,
    frequentMenuVersion,
    menuRegistry,
    pinnedPathSet,
    quickAccessPaths,
  ]);

  const handleToggleMenuPin = useCallback(
    (event: React.MouseEvent, path?: string | null) => {
      event.preventDefault();
      event.stopPropagation();
      if (!currentUserId || !path) return;
      const normalized = normalizeMenuPath(path);
      if (!normalized || quickAccessPaths.has(normalized)) return;
      togglePinnedMenuPath(currentUserId, normalized);
      setPinnedMenuVersion((version) => version + 1);
    },
    [currentUserId, quickAccessPaths]
  );

  const renderMenuPinButton = (
    path?: string | null,
    options?: { forceShow?: boolean; activeRow?: boolean; allowCollapsed?: boolean }
  ) => {
    if (!path || (isSidebarCollapsed && !options?.allowCollapsed)) return null;
    const normalized = normalizeMenuPath(path);
    if (!normalized || quickAccessPaths.has(normalized)) return null;
    const pinned = pinnedPathSet.has(normalized);
    return (
      <button
        type="button"
        title={pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
        aria-label={pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
        onClick={(event) => handleToggleMenuPin(event, normalized)}
        onMouseDown={(event) => event.stopPropagation()}
        className={`ml-auto flex-shrink-0 p-1 rounded transition-opacity ${
          pinned || options?.forceShow
            ? 'opacity-100'
            : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
        } ${
          options?.activeRow
            ? 'text-white/80 hover:text-white hover:bg-white/10'
            : pinned
              ? 'text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-950/40'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
        }`}
      >
        <PinIcon className={`w-3.5 h-3.5 ${pinned ? 'fill-current' : ''}`} />
      </button>
    );
  };

  const lastRecordedMenuPathRef = useRef<string | null>(null);

  useEffect(() => {
    lastRecordedMenuPathRef.current = null;
  }, [currentUserId]);

  useEffect(() => {
    if (!isLoggedIn || !currentUserId) return;
    const path = normalizeMenuPath(location.pathname);
    if (!menuRegistry.has(path)) return;
    if (lastRecordedMenuPathRef.current === path) return;
    lastRecordedMenuPathRef.current = path;
    recordMenuVisit(currentUserId, path);
    setFrequentMenuVersion((version) => version + 1);
  }, [currentUserId, isLoggedIn, location.pathname, menuRegistry]);

  // Close mobile menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setMobileMenuOpen(false);
      }
    }
    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClick);
    } else {
      document.removeEventListener('mousedown', handleClick);
    }
    return () => document.removeEventListener('mousedown', handleClick);
  }, [mobileMenuOpen]);

  // Track viewport width to activate only one NotificationsMenu instance
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = () => setIsLargeScreen(mq.matches);
    if ((mq as any).addEventListener) {
      (mq as any).addEventListener('change', handler);
    } else if ((mq as any).addListener) {
      (mq as any).addListener(handler);
    }
    handler();
    return () => {
      if ((mq as any).removeEventListener) {
        (mq as any).removeEventListener('change', handler);
      } else if ((mq as any).removeListener) {
        (mq as any).removeListener(handler);
      }
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    // Reset submenu states when closing
    setMobileSubMenus({
      'Clients': false,
      'Settings': false
    });
  }, [location.pathname]);

  // Mailbox: auto-collapse left nav for more reading space; restore previous state on leave
  useEffect(() => {
    const onMailbox = location.pathname === '/email';
    if (onMailbox) {
      setIsSidebarCollapsed((prev) => {
        if (sidebarBeforeMailboxRef.current === null) {
          sidebarBeforeMailboxRef.current = prev;
        }
        return true;
      });
      return;
    }
    if (sidebarBeforeMailboxRef.current !== null) {
      setIsSidebarCollapsed(sidebarBeforeMailboxRef.current);
      sidebarBeforeMailboxRef.current = null;
    }
  }, [location.pathname]);

  // Add this function to handle client deletion
  const handleDeleteClient = async (client: Client) => {
    if (!window.confirm(`Are you sure you want to delete client "${client.name}"? This action cannot be undone.`)) return;
    await fetch(`/api/clients/${client.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    fetchClients();
    navigate('/clients');
  };

  // Wrapper to fetch client by ID from URL and pass to ClientDetailPage (LEGACY - replaced by module-scope memoized version)
  const ClientDetailWrapperLegacy: React.FC<{ clients: Client[]; activityLogs: any[]; currentUserRoles: string[]; onEdit: (client: Client) => void; currentUsername: string | null; }> = React.memo(({ clients, activityLogs, currentUserRoles, onEdit, currentUsername }) => {
    const { id } = useParams<{ id: string }>();

    // Memoize client to prevent unnecessary re-renders when clients array reference changes
    const client = useMemo(() =>
      clients.find(c => c.id === id),
      [clients, id]
    );

    // Memoize callback functions to prevent creating new references on every render
    const handleBack = useCallback(() => window.history.back(), []);

    // Stabilize array props to prevent unnecessary re-renders
    const memoizedUserRoles = useMemo(() => currentUserRoles, [currentUserRoles.join(',')]);

    if (!client) return <div>Client not found</div>;

    return (
      <ClientDetailPage
        client={client}
        activityLogs={activityLogs}
        onBack={handleBack}
        onEdit={onEdit}
        onDelete={handleDeleteClient}
        currentUserRoles={memoizedUserRoles}
        currentUsername={currentUsername}
      />
    );
  }, (prevProps, nextProps) => {
    // Custom comparison function - return true if props are equal (skip re-render)
    // Get the current client ID from URL
    const urlId = window.location.pathname.split('/clients/')[1]?.split('/')[0];
    if (!urlId) {
      console.log('[ClientDetailWrapper React.memo] No URL ID found');
      return false;
    }

    // Find the specific client we're viewing in both prop sets
    const prevClient = prevProps.clients.find(c => c.id === urlId);
    const nextClient = nextProps.clients.find(c => c.id === urlId);

    // If client doesn't exist in either, let it re-render
    if (!prevClient && !nextClient) {
      console.log('[ClientDetailWrapper React.memo] Both clients missing');
      return true; // Both missing, no change
    }
    if (!prevClient || !nextClient) {
      console.log('[ClientDetailWrapper React.memo] One client missing');
      return false; // One missing, has changed
    }

    // Compare only relevant data
    const sameUsername = prevProps.currentUsername === nextProps.currentUsername;
    const sameRoles = prevProps.currentUserRoles.join(',') === nextProps.currentUserRoles.join(',');
    const sameClient = JSON.stringify(prevClient) === JSON.stringify(nextClient);

    const shouldSkipRender = sameUsername && sameRoles && sameClient;

    console.log('[ClientDetailWrapper React.memo]', {
      urlId,
      sameUsername,
      sameRoles,
      sameClient,
      shouldSkipRender
    });

    // Return true if everything is the same (skip re-render)
    return shouldSkipRender;
  });

  // Wrapper for WebManagerClientDetailPage with URL routing
  const WebManagerClientDetailWrapper: React.FC = () => {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();

    // Memoize the callback and roles to prevent unnecessary re-renders
    const handleBack = useCallback(() => navigate('/web-manager-clients'), [navigate]);
    const memoizedRoles = useMemo(() => currentUserRoles, [currentUserRoles.join(',')]);

    if (!clientId) return <div>Client ID not found</div>;
    return <WebManagerClientDetailPage clientId={clientId} onBack={handleBack} currentUserRoles={memoizedRoles} />;
  };

  // Wrapper for ModificationRequestDetailPage with URL routing
  const ModificationRequestDetailWrapper: React.FC = () => {
    const { requestId } = useParams<{ requestId: string }>();
    const navigate = useNavigate();

    const handleBack = useCallback(() => navigate(-1), [navigate]);
    const memoizedRoles = useMemo(() => currentUserRoles, [currentUserRoles.join(',')]);

    if (!requestId) return <div>Request ID not found</div>;
    return <ModificationRequestDetailPage requestId={requestId} onBack={handleBack} currentUserRoles={memoizedRoles} />;
  };

  // Add handleLogout if missing
  const handleLogout = async () => {
    // Clear FCM token before logout
    try {
      const fcmService = (await import('./services/fcmService')).default;
      fcmService.clearStoredToken();
    } catch (error) {
      console.error('Error clearing FCM token on logout:', error);
    }

    // Revoke REM Voice dialer JWT while session is still valid
    try {
      const { revokeRemVoiceSession } = await import('./contexts/RemVoiceContext');
      await revokeRemVoiceSession();
    } catch {
      /* ignore */
    }

    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    });
    setIsLoggedIn(false);
    setCurrentUserRoles([]);
    setCurrentUsername(null);
    setCurrentUserId(null);
    setCurrentUserName(null);
    setCurrentUserProfilePic(null);
    setRemVoiceAgentId(null);
    setPasskeyGate('checking');
  };

  // Add handleChangePassword if missing
  const handleChangePassword = async (oldPassword: string, newPassword: string) => {
    const res = await fetch('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ oldPassword, newPassword })
    });
    // After password change, re-fetch session to ensure still logged in
    await fetch('/api/auth/session', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (!data.loggedIn) setIsLoggedIn(false);
      });
    return res.json();
  };

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // WhatsApp unread count functionality
  const fetchWhatsAppUnreadCount = async () => {
    try {
      if (!isLoggedIn) return;
      const response = await fetch('/api/whatsapp/unread-count', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setWhatsappUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp unread count:', error);
    }
  };

  // Setup WhatsApp socket listeners and fetch initial count
  useEffect(() => {
    if (!isLoggedIn) return;

    // Fetch initial unread count
    fetchWhatsAppUnreadCount();

    // Setup socket connection for real-time updates
    let socket: any = null;

    try {
      // Check if socket.io is available (same pattern as WhatsAppPage)
      if (typeof (window as any).io === 'undefined') {
        return;
      }

      const socketUrl = import.meta.env.VITE_SOCKET_URL || (window.location.hostname === 'localhost' ?
        'http://localhost:4000' :
        window.location.origin);

      socket = (window as any).io(socketUrl, {
        withCredentials: true,
        autoConnect: true,
        transports: ['websocket', 'polling'],
        upgrade: true,
        rememberUpgrade: true,
        timeout: 20000,
        forceNew: false,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      // Listen for new messages
      socket.on('whatsapp:new_message', (data: { conversationId: string; message: any }) => {
        if (data.message.fromContact) {
          // Only increment for incoming messages from contacts
          setWhatsappUnreadCount(prev => prev + 1);
        }
      });

      // Listen for message read events
      socket.on('whatsapp:conversation_read', (data: { conversationId: string; userId: string; markedCount: number }) => {
        if (data.markedCount > 0) {
          setWhatsappUnreadCount(prev => Math.max(0, prev - data.markedCount));
        }
      });

      // Listen for conversation updates that might affect unread count
      socket.on('whatsapp:conversation_updated', () => {
        // Refresh count when conversations are updated
        fetchWhatsAppUnreadCount();
      });

    } catch (error) {
      console.error('Failed to initialize WhatsApp socket listeners:', error);
    }

    // Listen for page visibility changes to refresh count
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page became visible, refresh unread count
        fetchWhatsAppUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      if (socket) {
        socket.removeAllListeners('whatsapp:new_message');
        socket.removeAllListeners('whatsapp:conversation_read');
        socket.removeAllListeners('whatsapp:conversation_updated');
        socket.disconnect();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn]);

  // Refresh unread count when navigating back from WhatsApp page
  useEffect(() => {
    const handleFocus = () => {
      if (window.location.pathname !== '/whatsapp') {
        fetchWhatsAppUnreadCount();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isLoggedIn]);

  // After login: if user has no passkey, show dedicated setup page (every login)
  useEffect(() => {
    if (!isLoggedIn || !permissionsLoaded || !currentUserId) return;

    let cancelled = false;

    (async () => {
      try {
        const skipKey = `passkey_setup_skipped_${currentUserId}`;
        if (sessionStorage.getItem(skipKey) === '1') {
          if (!cancelled) setPasskeyGate('ok');
          return;
        }

        const res = await fetch('/api/auth/passkeys', { credentials: 'include' });
        if (!res.ok) {
          if (!cancelled) setPasskeyGate('ok');
          return;
        }
        const data = await res.json();
        if (data.remindersDisabled) {
          if (!cancelled) setPasskeyGate('ok');
          return;
        }
        const hasPasskey = Array.isArray(data.passkeys) && data.passkeys.length > 0;
        if (!cancelled) {
          setPasskeyGate(hasPasskey ? 'ok' : 'required');
        }
      } catch (err) {
        console.error('Passkey check failed:', err);
        if (!cancelled) setPasskeyGate('ok');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, permissionsLoaded, currentUserId]);

  // Only show loading if session is not checked yet
  if (!sessionChecked) {
    return <LoadingScreen message="Initializing..." showLogo={true} />;
  }

  // Check if current path is a public route
  const isPublicRoute = location.pathname.startsWith('/public/requirements/') || location.pathname.startsWith('/public/modifications/') || location.pathname.startsWith('/review/') || location.pathname.startsWith('/meeting-review/') || location.pathname.startsWith('/careers') || location.pathname.startsWith('/impersonate/') || location.pathname.startsWith('/portal/');

  if (!isLoggedIn && !isPublicRoute) {
    return (
      <LoginForm onLoginSuccess={handleLoginSuccess} />
    );
  }

  // Show loading while permissions are being fetched for logged-in users
  if (isLoggedIn && !permissionsLoaded) {
    return <LoadingScreen message="Loading Permissions" showLogo={true} />;
  }

  // Passkey setup gate — full-screen, no app chrome (for non-technical staff)
  if (isLoggedIn && permissionsLoaded && passkeyGate === 'checking') {
    return <LoadingScreen message="Preparing your account..." showLogo={true} />;
  }

  if (isLoggedIn && permissionsLoaded && passkeyGate === 'required') {
    return (
      <PasskeySetupPage
        userName={currentUserName || currentUsername}
        onComplete={() => setPasskeyGate('ok')}
        onSkip={() => {
          if (currentUserId) {
            try {
              sessionStorage.setItem(`passkey_setup_skipped_${currentUserId}`, '1');
            } catch {
              /* ignore */
            }
          }
          setPasskeyGate('ok');
        }}
        onNeverRemind={() => setPasskeyGate('ok')}
      />
    );
  }

  // If it's a public route, render without layout
  if (isPublicRoute) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
        <Route path="/public/requirements/:token" element={<PublicRequirementPage />} />
        <Route path="/public/modifications/:token" element={<PublicModificationPage />} />
        <Route path="/review/:token" element={<PublicReviewFormWrapper />} />
        <Route path="/meeting-review/:token" element={<MeetingReviewPage />} />
        <Route path="/careers" element={<PublicCareersPage />} />
        <Route path="/impersonate/:token" element={<ImpersonateLoginPage />} />
        <Route path="/portal/subscriptions" element={<CustomerSubscriptionPortal />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <ToastProvider>
      <PermissionProvider permissions={userPermissions} currentUserRoles={currentUserRoles} meetingPermissions={meetingPermissions}>
        <CurrencyProvider>
          <ThemeProvider>
            <RemVoiceProvider active={isLoggedIn} remVoiceAgentId={remVoiceAgentId}>
            <div className={`min-h-screen dark:bg-slate-950 ${location.pathname === '/email' ? 'bg-[#f0f4f9] max-w-[100vw] overflow-x-hidden' : 'bg-slate-100'}`}>
              <header className="bg-white shadow-sm sticky top-0 z-20">
                <div className="w-full px-3 sm:px-6 lg:px-8">
                  <div className="flex items-center py-2.5 sm:py-3 justify-between gap-2 flex-nowrap min-w-0">
                    {/* Logo */}
                    <div className="flex-shrink-0 flex items-center gap-2 min-w-0">
                      <ThemeLogo />
                    </div>

                    {/* Mobile hamburger menu button and user info */
                      // We only mount the NotificationsMenu with network side-effects when this header is visible
                    }
                    <div className="lg:hidden flex items-center gap-1 sm:gap-2 shrink-0 min-w-0">
                      {isLoggedIn && (
                        <div className="flex items-center gap-0.5 sm:gap-1.5 min-w-0">
                          <TasksMeetingsQuickAccess userId={currentUserId || ''} isActive={!isLargeScreen} />
                          <NotificationsMenu userId={currentUserId || ''} isActive={!isLargeScreen} />
                          <div className="flex items-center gap-1.5 text-sm shrink-0">
                            <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                              {currentUserProfilePic ? (
                                <img src={currentUserProfilePic} alt={currentUsername || ''} className="w-full h-full object-cover" />
                              ) : (
                                <UserIcon className="w-4 h-4 text-primary-500" />
                              )}
                            </div>
                            <span className="hidden sm:inline font-medium text-slate-700 truncate max-w-[7rem]">{currentUserName || currentUsername}</span>
                          </div>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="p-2 rounded-md text-slate-400 hover:text-slate-500 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 shrink-0"
                        aria-expanded="false"
                      >
                        <span className="sr-only">Open main menu</span>
                        {mobileMenuOpen ? (
                          <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        ) : (
                          <svg className="block h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
                          </svg>
                        )}
                      </button>
                    </div>

                    {/* Desktop Navigation and user menu */}
                    <div className="hidden lg:flex flex-1 items-center ml-8">
                      {/* Navigation links aligned left (hidden by default) */}
                      <nav className="hidden gap-1 items-center text-slate-700 font-medium flex-1">
                        {MENU_ITEMS.filter(item => {
                          // DevTracker: also show to users with dev/requester access
                          if (item.label === 'Development' && hasDevTrackerAccess) return true;
                          if (item.submenu) {
                            // For items with submenus, only show if user has access to at least one submenu item
                            return hasAnySubmenuPermission(userPermissions, item.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                          }
                          // For items without submenus, check their own permission
                          return hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                        }).map(item =>
                          item.submenu ? (
                            <div className="relative" ref={item.label === 'Clients' ? clientsMenuRef : item.label === 'Tickets' ? ticketsMenuRef : item.label === 'Reports' ? reportsMenuRef : item.label === 'Accounting' ? accountingMenuRef : null} key={item.label}>
                              <button
                                className="flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors text-slate-500 hover:bg-slate-200 hover:text-slate-700 focus:outline-none"
                                onClick={e => {
                                  e.preventDefault();
                                  if (item.label === 'Clients') {
                                    setClientsMenuOpen(v => !v);
                                    setTicketsMenuOpen(false); // Close Tickets menu when opening Clients menu
                                    setReportsMenuOpen(false);
                                  } else if (item.label === 'Tickets') {
                                    setTicketsMenuOpen(v => !v);
                                    setClientsMenuOpen(false); // Close Clients menu when opening Tickets menu
                                    setReportsMenuOpen(false);
                                  } else if (item.label === 'Reports') {
                                    setReportsMenuOpen(v => !v);
                                    setClientsMenuOpen(false);
                                    setTicketsMenuOpen(false);
                                    setAccountingMenuOpen(false);
                                  } else if (item.label === 'Accounting') {
                                    setAccountingMenuOpen(v => !v);
                                    setClientsMenuOpen(false);
                                    setTicketsMenuOpen(false);
                                    setReportsMenuOpen(false);
                                  }
                                }}
                                aria-haspopup="true"
                                aria-expanded={item.label === 'Clients' ? clientsMenuOpen : item.label === 'Tickets' ? ticketsMenuOpen : item.label === 'Reports' ? reportsMenuOpen : item.label === 'Accounting' ? accountingMenuOpen : false}
                              >
                                {item.icon && <item.icon className="w-5 h-5 mr-1" />}
                                <span>{item.label}</span>
                                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              </button>
                              {((item.label === 'Clients' && clientsMenuOpen) || (item.label === 'Tickets' && ticketsMenuOpen) || (item.label === 'Reports' && reportsMenuOpen) || (item.label === 'Accounting' && accountingMenuOpen)) && (
                                <div className="absolute left-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-40 flex flex-col gap-1">
                                  {item.submenu.filter(sub => {
                                    // Special case: Permissions menu item should only show for Superadmin
                                    if (sub.label === 'Permissions' && sub.to === '/meetings/permissions') {
                                      return currentUserRoles.includes('Superadmin');
                                    }
                                    // DevTracker: show Requests/Board to dev/requester users; Settings+Releases only to admins
                                    if (sub.permission?.section === 'DevTracker' && hasDevTrackerAccess && sub.label !== 'Settings' && sub.label !== 'Releases') return true;
                                    return hasPermission(userPermissions, sub.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                                  }).map(sub => (
                                    <a
                                      href={sub.to}
                                      key={sub.label}
                                      onClick={e => {
                                        e.preventDefault();
                                        navigate(sub.to);
                                        setClientsMenuOpen(false);

                                        setTicketsMenuOpen(false);
                                        setReportsMenuOpen(false);
                                        setAccountingMenuOpen(false);
                                      }}
                                      className={`flex items-center gap-2 block w-full text-left px-4 py-2 text-sm rounded-md transition-colors ${location.pathname === sub.to ? 'bg-primary-100 text-primary-700' : 'text-slate-700 hover:bg-slate-100'}`}
                                    >
                                      <span>{sub.label}</span>
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <NavLink to={item.to} key={item.label}>
                              <div className="flex items-center gap-1 relative">
                                {item.icon && <item.icon className="w-5 h-5 mr-1" />}
                                <span>{item.label}</span>
                                {/* Add badge for WhatsApp unread count */}
                                {item.label === 'WhatsApp' && whatsappUnreadCount > 0 && (
                                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 font-bold shadow-lg">
                                    {whatsappUnreadCount > 99 ? '99+' : whatsappUnreadCount}
                                  </div>
                                )}
                              </div>
                            </NavLink>
                          )
                        )}
                        {/* Admin Controls dropdown menu - hidden by default */}
                        {ADMIN_MENU_ITEMS.some(item => hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions)) && (
                          <div className="relative hidden" ref={adminMenuRef}>
                            <button
                              className="px-3 py-2 text-sm font-medium rounded-md transition-colors text-slate-500 hover:bg-slate-200 hover:text-slate-700 flex items-center gap-1"
                              onClick={() => setAdminMenuOpen(v => !v)}
                              aria-haspopup="true"
                              aria-expanded={adminMenuOpen}
                            >
                              <CogIcon className="w-5 h-5 mr-1" />
                              Settings
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {adminMenuOpen && (
                              <div className="absolute left-0 mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-40">
                                {ADMIN_MENU_ITEMS.filter(item => hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions)).map(item => (
                                  <button
                                    key={item.label}
                                    onClick={() => { navigate(item.to); setAdminMenuOpen(false); }}
                                    className={`flex items-center gap-2 block w-full text-left px-4 py-2 text-sm ${location.pathname === item.to ? 'bg-primary-100 text-primary-700' : 'text-slate-700 hover:bg-slate-100'}`}
                                  >
                                    <CogIcon className="w-5 h-5 mr-1" />
                                    <span>{item.label}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </nav>
                      {/* User actions and notifications aligned right - always show if logged in */
                        // Desktop header renders its own instance; mark it active only on large screens via CSS media query detection from React is tricky,
                        // so we keep both instances light and let the prop control activation. For desktop we pass true as well since only one is mounted here.
                      }
                      {isLoggedIn && (
                        <div className="flex items-center gap-2 ml-auto">

                          {/* Dark Mode Toggle */}
                          <DarkModeToggle />

                          {/* AI Assistant Button (Superadmin only) */}
                          {currentUserRoles.includes('Superadmin') && (
                            <button
                              onClick={() => setIsAIChatOpen(true)}
                              className="relative p-2 rounded-lg transition-colors bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white hover:from-indigo-700 hover:via-purple-700 hover:to-pink-600 shadow-lg shadow-purple-500/30"
                              title="AI Assistant"
                            >
                              <SparklesIcon className="w-5 h-5" />
                            </button>
                          )}
                          {/* Right side menu icons */}
                          {RIGHT_MENU_ITEMS.filter(item => hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions)).map(item => (
                            <button
                              key={item.label}
                              onClick={() => navigate(item.to)}
                              className={`relative p-2 rounded-lg transition-colors ${location.pathname === item.to ? 'bg-primary-100 text-primary-600' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
                              title={item.label}
                            >
                              {item.icon && <item.icon className="w-5 h-5" />}
                              {/* Show unread count badge for WhatsApp */}
                              {item.label === 'WhatsApp' && whatsappUnreadCount > 0 && (
                                <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold shadow-lg">
                                  <span className="animate-pulse">
                                    {whatsappUnreadCount > 99 ? '99+' : whatsappUnreadCount}
                                  </span>
                                </div>
                              )}
                            </button>
                          ))}
                          <TasksMeetingsQuickAccess userId={currentUserId || ''} isActive={isLargeScreen} />
                          <NotificationsMenu userId={currentUserId || ''} isActive={isLargeScreen} />
                          <div className="relative" ref={userMenuRef}>
                            <button
                              className="flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 border border-slate-200 shadow-sm hover:bg-slate-200 focus:outline-none"
                              onClick={() => setIsUserMenuOpen((v) => !v)}
                              aria-haspopup="true"
                              aria-expanded={isUserMenuOpen}
                            >
                              <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                                {currentUserProfilePic ? (
                                  <img src={currentUserProfilePic} alt={currentUsername || ''} className="w-full h-full object-cover" />
                                ) : (
                                  <UserIcon className="w-4 h-4 text-primary-500" />
                                )}
                              </div>
                              <div className="flex flex-col items-start">
                                <span className="font-semibold text-slate-800 text-sm leading-tight">{currentUserName || currentUsername}</span>
                                <span className="text-[10px] text-slate-500 font-medium leading-tight">{currentUserRoles[0]}</span>
                              </div>
                              <svg className="w-4 h-4 ml-1 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {isUserMenuOpen && (
                              <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-40">
                                <button onClick={() => { navigate('/profile'); setIsUserMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100">View Profile</button>
                                <button onClick={() => { handleLogout(); setIsUserMenuOpen(false); }} className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">Logout</button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Mobile menu panel */}
                    {mobileMenuOpen && (
                      <div className="lg:hidden" ref={mobileMenuRef}>
                        <div className="absolute top-full left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-30 max-h-[calc(100vh-80px)]">
                          <div className="overflow-y-auto max-h-[calc(100vh-80px)] px-4 py-2 space-y-1">
                            {/* Main Menu Items */}
                            {MENU_ITEMS.filter(item => {
                              // DevTracker: also show to users with dev/requester access
                              if (item.label === 'Development' && hasDevTrackerAccess) return true;
                              if (item.submenu) {
                                // For items with submenus, only show if user has access to at least one submenu item
                                return hasAnySubmenuPermission(userPermissions, item.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                              }
                              // For items without submenus, check their own permission
                              return hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                            }).map(item =>
                              item.submenu ? (
                                <div key={item.label} className="space-y-1">
                                  <button
                                    onClick={() => setMobileSubMenus(prev => ({ ...prev, [item.label]: !prev[item.label] }))}
                                    className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      {item.icon && <item.icon className="w-4 h-4" />}
                                      <span>{item.label}</span>
                                    </div>
                                    <svg
                                      className={`w-4 h-4 transition-transform ${mobileSubMenus[item.label] ? 'rotate-180' : ''}`}
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {mobileSubMenus[item.label] && (
                                    <div className="pl-6 space-y-1">
                                      {item.submenu.filter(sub => {
                                        // If item has submenu, check if user has access to any nested item
                                        if (sub.submenu) {
                                          return hasAnySubmenuPermission(userPermissions, sub.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                                        }
                                        // DevTracker: show Requests/Board to dev/requester users; Settings+Releases only to admins
                                        if (sub.permission?.section === 'DevTracker' && hasDevTrackerAccess && sub.label !== 'Settings' && sub.label !== 'Releases') return true;
                                        // Otherwise check the item's own permission
                                        return hasPermission(userPermissions, sub.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                                      }).map(sub => (
                                        sub.submenu ? (
                                          // Level 3 menu item (has submenu) - in mobile mode
                                          <div key={sub.label} className="space-y-1">
                                            <button
                                              onClick={() => setMobileSubMenus(prev => ({ ...prev, [`${item.label}.${sub.label}`]: !prev[`${item.label}.${sub.label}`] }))}
                                              className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                            >
                                              <span>{sub.label}</span>
                                              <svg
                                                className={`w-3 h-3 transition-transform ${mobileSubMenus[`${item.label}.${sub.label}`] ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                viewBox="0 0 24 24"
                                              >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                              </svg>
                                            </button>
                                            {mobileSubMenus[`${item.label}.${sub.label}`] && (
                                              <div className="pl-4 space-y-1">
                                                {sub.submenu.filter(subSub => hasPermission(userPermissions, subSub.permission, currentUserRoles, permissionsLoaded, meetingPermissions)).map(subSub => (
                                                  <button
                                                    key={subSub.label}
                                                    onClick={() => {
                                                      navigate(subSub.to);
                                                      setMobileMenuOpen(false);
                                                    }}
                                                    className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === subSub.to ? 'bg-primary-100 text-primary-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                                  >
                                                    {subSub.label}
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          // Level 2 menu item (no submenu) - in mobile mode
                                          <button
                                            key={sub.label}
                                            onClick={() => {
                                              navigate(sub.to);
                                              setMobileMenuOpen(false);
                                            }}
                                            className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === sub.to ? 'bg-primary-100 text-primary-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                          >
                                            {sub.label}
                                          </button>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <button
                                  key={item.label}
                                  onClick={() => {
                                    navigate(item.to);
                                    setMobileMenuOpen(false);
                                  }}
                                  className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === item.to ? 'bg-primary-100 text-primary-700' : 'text-slate-700 hover:bg-slate-50'}`}
                                >
                                  {item.icon && <item.icon className="w-4 h-4" />}
                                  <span>{item.label}</span>
                                  {/* Add badge for WhatsApp unread count in mobile */}
                                  {item.label === 'WhatsApp' && whatsappUnreadCount > 0 && (
                                    <div className="ml-auto bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-bold">
                                      {whatsappUnreadCount > 99 ? '99+' : whatsappUnreadCount}
                                    </div>
                                  )}
                                </button>
                              )
                            )}

                            {/* Admin Controls in mobile */}
                            {ADMIN_MENU_ITEMS.some(item => {
                              // If item has submenu, check if user has access to any nested item
                              if (item.submenu) {
                                return hasAnySubmenuPermission(userPermissions, item.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                              }
                              // Otherwise check the item's own permission
                              return hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                            }) && (
                                <div className="space-y-1 border-t border-slate-200 pt-2">
                                  <button
                                    onClick={() => setMobileSubMenus(prev => ({ ...prev, 'Settings': !prev['Settings'] }))}
                                    className="flex items-center justify-between w-full px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                  >
                                    <div className="flex items-center gap-2">
                                      <CogIcon className="w-4 h-4" />
                                      <span>Settings</span>
                                    </div>
                                    <svg
                                      className={`w-4 h-4 transition-transform ${mobileSubMenus['Settings'] ? 'rotate-180' : ''}`}
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {mobileSubMenus['Settings'] && (
                                    <div className="pl-6 space-y-1">
                                      {ADMIN_MENU_ITEMS.filter(item => {
                                        // If item has submenu, check if user has access to any nested item
                                        if (item.submenu) {
                                          return hasAnySubmenuPermission(userPermissions, item.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                                        }
                                        // Otherwise check the item's own permission
                                        return hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                                      }).map(item => (
                                        item.submenu ? (
                                          // Level 3 menu item under Settings (has submenu) - in mobile mode
                                          <div key={item.label} className="space-y-1">
                                            <button
                                              onClick={() => setMobileSubMenus(prev => ({ ...prev, [`Settings.${item.label}`]: !prev[`Settings.${item.label}`] }))}
                                              className="flex items-center justify-between w-full px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                            >
                                              <span>{item.label}</span>
                                              <svg
                                                className={`w-3 h-3 transition-transform ${mobileSubMenus[`Settings.${item.label}`] ? 'rotate-180' : ''}`}
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                viewBox="0 0 24 24"
                                              >
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                              </svg>
                                            </button>
                                            {mobileSubMenus[`Settings.${item.label}`] && (
                                              <div className="pl-4 space-y-1">
                                                {item.submenu.filter(subItem => hasPermission(userPermissions, subItem.permission, currentUserRoles, permissionsLoaded)).map(subItem => (
                                                  <button
                                                    key={subItem.label}
                                                    onClick={() => {
                                                      navigate(subItem.to);
                                                      setMobileMenuOpen(false);
                                                    }}
                                                    className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === subItem.to ? 'bg-primary-100 text-primary-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                                  >
                                                    {subItem.label}
                                                  </button>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          // Level 2 menu item under Settings (no submenu) - in mobile mode
                                          <button
                                            key={item.label}
                                            onClick={() => {
                                              navigate(item.to);
                                              setMobileMenuOpen(false);
                                            }}
                                            className={`block w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === item.to ? 'bg-primary-100 text-primary-700' : 'text-slate-600 hover:bg-slate-50'}`}
                                          >
                                            {item.label}
                                          </button>
                                        )
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}

                            {/* Right Menu Items in mobile */}
                            <div className="border-t border-slate-200 pt-2 space-y-1">
                              <div className="px-3 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Quick Access</div>
                              {RIGHT_MENU_ITEMS.filter(item => hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions)).map(item => (
                                <button
                                  key={item.label}
                                  onClick={() => {
                                    navigate(item.to);
                                    setMobileMenuOpen(false);
                                  }}
                                  className={`flex items-center gap-2 w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === item.to ? 'bg-primary-100 text-primary-700' : 'text-slate-700 hover:bg-slate-50'}`}
                                >
                                  {item.icon && <item.icon className="w-4 h-4" />}
                                  <span>{item.label}</span>
                                  {/* Show unread count badge for WhatsApp in mobile */}
                                  {item.label === 'WhatsApp' && whatsappUnreadCount > 0 && (
                                    <div className="ml-auto bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold">
                                      {whatsappUnreadCount > 99 ? '99+' : whatsappUnreadCount}
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>

                            {/* User Profile Section in mobile */}
                            {isLoggedIn && (
                              <div className="border-t border-slate-200 pt-2 space-y-1">
                                <div className="px-3 py-1 text-xs font-semibold text-slate-500 uppercase tracking-wide">Account</div>
                                <div className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-50 rounded-md">
                                  <UserIcon className="w-4 h-4 text-primary-500" />
                                  <span className="font-semibold text-slate-800">{currentUsername}</span>
                                  <span className="text-xs text-slate-500">({currentUserRoles[0]})</span>
                                </div>
                                <button
                                  onClick={() => {
                                    navigate('/profile');
                                    setMobileMenuOpen(false);
                                  }}
                                  className="block w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                                >
                                  View Profile
                                </button>
                                <button
                                  onClick={() => {
                                    handleLogout();
                                    setMobileMenuOpen(false);
                                  }}
                                  className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                >
                                  Logout
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </header>
              <div className="w-full px-0 sm:px-0 lg:px-0 py-0">
                <div className="flex relative">
                  <aside className={`hidden lg:block bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 ${isSidebarCollapsed ? 'w-16 min-w-16 max-w-16' : 'w-64 min-w-64 max-w-64'} transition-[width] duration-200 z-10 sticky top-[64px] h-[calc(100vh-64px)] shrink-0`}>
                    <div className="flex flex-col h-full min-h-0 overflow-hidden">
                      <nav className={`space-y-1 flex-1 min-h-0 overflow-y-auto overflow-x-hidden ${isSidebarCollapsed ? 'p-2' : 'p-3'}`}>
                        {/* Main items: Dashboard, Calendar, WhatsApp, Mailbox */}
                        {hasPermission(userPermissions, { module: 'Dashboard', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) && (
                          <button
                            onClick={() => { navigate('/dashboard'); }}
                            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === '/dashboard' ? getMenuStyle('Dashboard').active : 'text-slate-700'} ${location.pathname !== '/dashboard' ? getMenuStyle('Dashboard').hover : ''}`}
                          >
                            <GlobeIcon className={`w-5 h-5 ${location.pathname === '/dashboard' ? 'text-white' : getMenuStyle('Dashboard').icon}`} />
                            {!isSidebarCollapsed && <span>Dashboard</span>}
                          </button>
                        )}
                        {hasPermission(userPermissions, { module: 'Calendar', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) && (
                          <button
                            onClick={() => { navigate('/calendar'); }}
                            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === '/calendar' ? getMenuStyle('Calendar').active : 'text-slate-700'} ${location.pathname !== '/calendar' ? getMenuStyle('Calendar').hover : ''}`}
                          >
                            <CalendarIcon className={`w-5 h-5 ${location.pathname === '/calendar' ? 'text-white' : getMenuStyle('Calendar').icon}`} />
                            {!isSidebarCollapsed && <span>Calendar</span>}
                          </button>
                        )}
                        {hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) && (
                          <button
                            onClick={() => { navigate('/whatsapp-support'); }}
                            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === '/whatsapp-support' ? getMenuStyle('WhatsApp').active : 'text-slate-700'} ${location.pathname !== '/whatsapp-support' ? getMenuStyle('WhatsApp').hover : ''}`}
                          >
                            <WhatsAppIcon className={`w-5 h-5 ${location.pathname === '/whatsapp-support' ? 'text-white' : getMenuStyle('WhatsApp').icon}`} />
                            {!isSidebarCollapsed && <span>WhatsApp Support</span>}
                          </button>
                        )}
                        {hasPermission(userPermissions, { module: 'Email', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) && (
                          <button
                            onClick={() => { navigate('/email'); }}
                            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === '/email' || location.pathname === '/email-templates' ? getMenuStyle('Mailbox').active : 'text-slate-700'} ${location.pathname !== '/email' && location.pathname !== '/email-templates' ? getMenuStyle('Mailbox').hover : ''}`}
                          >
                            <InboxIcon className={`w-5 h-5 ${location.pathname === '/email' || location.pathname === '/email-templates' ? 'text-white' : getMenuStyle('Mailbox').icon}`} />
                            {!isSidebarCollapsed && <span>Mailbox</span>}
                          </button>
                        )}
                        {hasPermission(userPermissions, { module: 'Tasks', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) && (
                          <button
                            onClick={() => { navigate('/tasks'); }}
                            className={`group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${location.pathname === '/tasks' ? getMenuStyle('Tasks').active : 'text-slate-700'} ${location.pathname !== '/tasks' ? getMenuStyle('Tasks').hover : ''}`}
                          >
                            <ListTodoIcon className={`w-5 h-5 ${location.pathname === '/tasks' ? 'text-white' : getMenuStyle('Tasks').icon}`} />
                            {!isSidebarCollapsed && <span>Tasks</span>}
                          </button>
                        )}

                        {(pinnedMenuEntries.length > 0 || mostUsedMenuEntries.length > 0) && (
                          <div className={`${isSidebarCollapsed ? 'space-y-0.5' : 'pt-1.5 mt-1.5 border-t border-slate-200 dark:border-slate-700 space-y-0.5'}`}>
                            {pinnedMenuEntries.map((entry) => {
                              const styleKey = MENU_STYLES[entry.label] ? entry.label : entry.styleKey;
                              const style = getMenuStyle(styleKey);
                              const Icon =
                                menuIconByStyleKey.get(entry.label) ||
                                menuIconByStyleKey.get(entry.styleKey);
                              const isActive = location.pathname === entry.path;
                              const subtitle =
                                entry.parentLabel && entry.parentLabel !== entry.label
                                  ? entry.parentLabel
                                  : null;
                              return (
                                <div
                                  key={`pinned-${entry.path}`}
                                  className={`group flex items-center rounded-md transition-colors ${isActive ? style.active : 'text-slate-700'} ${!isActive ? style.hover : ''}`}
                                >
                                  <button
                                    onClick={(e) => handleNavigation(e, entry.path)}
                                    onMouseDown={(e) => e.button === 1 && handleNavigation(e, entry.path)}
                                    onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, entry.path); }}
                                    title={subtitle ? `${entry.label} (${subtitle})` : entry.label}
                                    className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} min-w-0 flex-1 text-left px-3 py-1.5 text-sm`}
                                  >
                                    {Icon && (
                                      <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : style.icon}`} />
                                    )}
                                    {!isSidebarCollapsed && (
                                      <span className="min-w-0">
                                        <span className="block truncate font-medium leading-tight">{entry.label}</span>
                                        {subtitle && (
                                          <span className={`block truncate text-[10px] leading-tight ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                                            {subtitle}
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </button>
                                  {renderMenuPinButton(entry.path, { forceShow: true, activeRow: isActive })}
                                </div>
                              );
                            })}

                            {mostUsedMenuEntries.length > 0 && (
                              <>
                                {!isSidebarCollapsed && (
                                  <div className="px-3 pt-1.5 pb-0.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                    <StarIcon className="w-3 h-3" />
                                    <span>Frequently Used</span>
                                  </div>
                                )}
                                {mostUsedMenuEntries.map((entry) => {
                                  const styleKey = MENU_STYLES[entry.label] ? entry.label : entry.styleKey;
                                  const style = getMenuStyle(styleKey);
                                  const Icon =
                                    menuIconByStyleKey.get(entry.label) ||
                                    menuIconByStyleKey.get(entry.styleKey);
                                  const isActive = location.pathname === entry.path;
                                  const subtitle =
                                    entry.parentLabel && entry.parentLabel !== entry.label
                                      ? entry.parentLabel
                                      : null;
                                  return (
                                    <div
                                      key={entry.path}
                                      className={`group flex items-center rounded-md transition-colors ${isActive ? style.active : 'text-slate-700'} ${!isActive ? style.hover : ''}`}
                                    >
                                      <button
                                        onClick={(e) => handleNavigation(e, entry.path)}
                                        onMouseDown={(e) => e.button === 1 && handleNavigation(e, entry.path)}
                                        onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, entry.path); }}
                                        title={subtitle ? `${entry.label} (${subtitle})` : entry.label}
                                        className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} min-w-0 flex-1 text-left px-3 py-1.5 text-sm`}
                                      >
                                        {Icon && (
                                          <Icon className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-white' : style.icon}`} />
                                        )}
                                        {!isSidebarCollapsed && (
                                          <span className="min-w-0">
                                            <span className="block truncate font-medium leading-tight">{entry.label}</span>
                                            {subtitle && (
                                              <span className={`block truncate text-[10px] leading-tight ${isActive ? 'text-white/80' : 'text-slate-400'}`}>
                                                {subtitle}
                                              </span>
                                            )}
                                          </span>
                                        )}
                                      </button>
                                      {renderMenuPinButton(entry.path, { activeRow: isActive })}
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </div>
                        )}

                        {MENU_ITEMS.filter(item => {
                          if (item.label === 'Dashboard') return false;
                          // DevTracker: also show to users with dev/requester access
                          if (item.label === 'Development' && hasDevTrackerAccess) return true;
                          if (item.submenu) {
                            // For items with submenus, only show if user has access to at least one submenu item
                            return hasAnySubmenuPermission(userPermissions, item.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                          }
                          // For items without submenus, check their own permission
                          return hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                        }).map(item =>
                          item.submenu ? (
                            <div key={item.label} className={`space-y-1 ${isSidebarCollapsed ? 'relative' : ''}`}>
                              <button
                                onClick={(e) => handleMenuClick(e, item.label)}
                                className={`menu-button group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} w-full px-3 py-2 text-sm font-medium text-slate-700 ${getMenuStyle(item.label).hover} rounded-md transition-colors`}
                              >
                                <div className={`flex items-center ${isSidebarCollapsed ? '' : 'gap-2'}`}>
                                  {item.icon && <item.icon className={`w-5 h-5 ${getMenuStyle(item.label).icon}`} />}
                                  {!isSidebarCollapsed && <span>{item.label}</span>}
                                </div>
                                {!isSidebarCollapsed && (
                                  <svg
                                    className={`w-4 h-4 transition-transform ${leftNavSubMenus[item.label] ? 'rotate-180' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                  </svg>
                                )}
                              </button>
                              {leftNavSubMenus[item.label] && !isSidebarCollapsed && (
                                <div className="pl-6 space-y-1">
                                  {item.submenu.filter(sub => {
                                    // Special case: Permissions menu item should only show for Superadmin
                                    if (sub.label === 'Permissions' && sub.to === '/meetings/permissions') {
                                      return currentUserRoles.includes('Superadmin');
                                    }
                                    // If item has submenu, check if user has access to any nested item
                                    if (sub.submenu) {
                                      return hasAnySubmenuPermission(userPermissions, sub.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                                    }
                                    // DevTracker: show Requests/Board to dev/requester users; Settings+Releases only to admins
                                    if (sub.permission?.section === 'DevTracker' && hasDevTrackerAccess && sub.label !== 'Settings' && sub.label !== 'Releases') return true;
                                    // Otherwise check the item's own permission
                                    return hasPermission(userPermissions, sub.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                                  }).map(sub => (
                                    sub.submenu ? (
                                      // Level 3 menu item (has submenu)
                                      <div key={sub.label} className="space-y-1">
                                        <button
                                          onClick={(e) => handleMenuClick(e, `${item.label}.${sub.label}`)}
                                          className={`flex items-center justify-between w-full text-left px-3 py-2 text-sm rounded-md transition-colors text-slate-600 hover:bg-slate-50`}
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className={`w-1.5 h-1.5 rounded-full ${getMenuStyle(item.label).bullet}`}></span>
                                            <span>{sub.label}</span>
                                          </div>
                                          <svg
                                            className={`w-3 h-3 transition-transform ${leftNavSubMenus[`${item.label}.${sub.label}`] ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                          </svg>
                                        </button>
                                        {leftNavSubMenus[`${item.label}.${sub.label}`] && (
                                          <div className="pl-6 space-y-1">
                                            {sub.submenu.filter(subSub => hasPermission(userPermissions, subSub.permission, currentUserRoles, permissionsLoaded, meetingPermissions)).map(subSub => {
                                              const isActive = location.pathname === subSub.to;
                                              return (
                                                <div
                                                  key={subSub.label}
                                                  className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle(item.label).subActive : 'text-slate-600 hover:bg-slate-50'}`}
                                                >
                                                  <button
                                                    onClick={(e) => handleNavigation(e, subSub.to)}
                                                    onMouseDown={(e) => e.button === 1 && handleNavigation(e, subSub.to)}
                                                    onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, subSub.to); }}
                                                    className="flex items-center gap-2 min-w-0 flex-1 text-left px-3 py-2 text-sm"
                                                  >
                                                    <span className={`w-1 h-1 rounded-full ${getMenuStyle(item.label).bullet}`}></span>
                                                    <span className="truncate">{subSub.label}</span>
                                                  </button>
                                                  {renderMenuPinButton(subSub.to, { activeRow: isActive })}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      // Level 2 menu item (no submenu)
                                      (() => {
                                        const isActive = location.pathname === sub.to;
                                        return (
                                          <div
                                            key={sub.label}
                                            className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle(item.label).subActive : 'text-slate-600 hover:bg-slate-50'}`}
                                          >
                                            <button
                                              onClick={(e) => handleNavigation(e, sub.to)}
                                              onMouseDown={(e) => e.button === 1 && handleNavigation(e, sub.to)}
                                              onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, sub.to); }}
                                              className="flex items-center gap-2 min-w-0 flex-1 text-left px-3 py-2 text-sm"
                                            >
                                              <span className={`w-1.5 h-1.5 rounded-full ${getMenuStyle(item.label).bullet}`}></span>
                                              <span className="truncate">{sub.label}</span>
                                            </button>
                                            {renderMenuPinButton(sub.to, { activeRow: isActive })}
                                          </div>
                                        );
                                      })()
                                    )
                                  ))}
                                </div>
                              )}
                              {isSidebarCollapsed && clickedMenuItem === item.label && (
                                <div
                                  className="submenu-container fixed w-56 bg-white border border-slate-200 rounded-lg shadow-xl py-2 max-h-[80vh] overflow-y-auto"
                                  style={{
                                    zIndex: 99999,
                                    top: `${submenuPosition.top}px`,
                                    left: `${submenuPosition.left}px`
                                  }}
                                >
                                  {item.submenu.filter(sub => {
                                    // Special case: Permissions menu item should only show for Superadmin
                                    if (sub.label === 'Permissions' && sub.to === '/meetings/permissions') {
                                      return currentUserRoles.includes('Superadmin');
                                    }
                                    // If item has submenu, check if user has access to any nested item
                                    if (sub.submenu) {
                                      return hasAnySubmenuPermission(userPermissions, sub.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                                    }
                                    // DevTracker: show Requests/Board to dev/requester users; Settings+Releases only to admins
                                    if (sub.permission?.section === 'DevTracker' && hasDevTrackerAccess && sub.label !== 'Settings' && sub.label !== 'Releases') return true;
                                    // Otherwise check the item's own permission
                                    return hasPermission(userPermissions, sub.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                                  }).map(sub => (
                                    sub.submenu ? (
                                      // Level 3 menu item (has submenu) - in collapsed mode
                                      <div key={sub.label} className="space-y-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleMenuClick(e, `${item.label}.${sub.label}`);
                                          }}
                                          className={`flex items-center justify-between w-full text-left px-4 py-2 text-sm rounded-md transition-colors text-slate-700 hover:bg-slate-100`}
                                        >
                                          <span>{sub.label}</span>
                                          <svg
                                            className={`w-3 h-3 transition-transform ${leftNavSubMenus[`${item.label}.${sub.label}`] ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                          >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                          </svg>
                                        </button>
                                        {leftNavSubMenus[`${item.label}.${sub.label}`] && (
                                          <div className="pl-4 space-y-1">
                                            {sub.submenu.filter(subSub => hasPermission(userPermissions, subSub.permission, currentUserRoles, permissionsLoaded, meetingPermissions)).map(subSub => {
                                              const isActive = location.pathname === subSub.to;
                                              return (
                                                <div
                                                  key={subSub.label}
                                                  className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle(item.label).subActive : 'text-slate-700 hover:bg-slate-100'}`}
                                                >
                                                  <button
                                                    onClick={(e) => handleNavigation(e, subSub.to)}
                                                    onMouseDown={(e) => e.button === 1 && handleNavigation(e, subSub.to)}
                                                    onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, subSub.to); }}
                                                    className="flex items-center gap-2 min-w-0 flex-1 text-left px-4 py-2 text-sm"
                                                  >
                                                    <span className="text-xs">•</span>
                                                    <span className="truncate">{subSub.label}</span>
                                                  </button>
                                                  {renderMenuPinButton(subSub.to, { activeRow: isActive, allowCollapsed: true })}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      // Level 2 menu item (no submenu) - in collapsed mode
                                      (() => {
                                        const isActive = location.pathname === sub.to;
                                        return (
                                          <div
                                            key={sub.label}
                                            className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle(item.label).subActive : 'text-slate-700 hover:bg-slate-100'}`}
                                          >
                                            <button
                                              onClick={(e) => handleNavigation(e, sub.to)}
                                              onMouseDown={(e) => e.button === 1 && handleNavigation(e, sub.to)}
                                              onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, sub.to); }}
                                              className="flex items-center gap-2 min-w-0 flex-1 text-left px-4 py-2 text-sm"
                                            >
                                              <span className="truncate">{sub.label}</span>
                                            </button>
                                            {renderMenuPinButton(sub.to, { activeRow: isActive, allowCollapsed: true })}
                                          </div>
                                        );
                                      })()
                                    )
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            (() => {
                              const isActive = location.pathname === item.to;
                              return (
                                <div
                                  key={item.label}
                                  className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle(item.label).active : 'text-slate-700'} ${!isActive ? getMenuStyle(item.label).hover : ''}`}
                                >
                                  <button
                                    onClick={(e) => handleNavigation(e, item.to)}
                                    onMouseDown={(e) => e.button === 1 && handleNavigation(e, item.to)}
                                    onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, item.to); }}
                                    className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-2'} min-w-0 flex-1 text-left px-3 py-2 text-sm`}
                                  >
                                    {item.icon && <item.icon className={`w-5 h-5 ${isActive ? 'text-white' : getMenuStyle(item.label).icon}`} />}
                                    {!isSidebarCollapsed && <span className="truncate">{item.label}</span>}
                                  </button>
                                  {renderMenuPinButton(item.to, { activeRow: isActive })}
                                </div>
                              );
                            })()
                          )
                        )}
                        {ADMIN_MENU_ITEMS.some(item => hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions)) && (
                          <div className={`space-y-1 border-t border-slate-200 pt-2 ${isSidebarCollapsed ? 'relative' : ''}`}>
                            <button
                              onClick={(e) => handleMenuClick(e, 'Settings')}
                              className={`menu-button group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} w-full px-3 py-2 text-sm font-medium text-slate-700 ${getMenuStyle('Settings').hover} rounded-md transition-colors`}
                            >
                              <div className={`flex items-center ${isSidebarCollapsed ? '' : 'gap-2'}`}>
                                <CogIcon className={`w-5 h-5 ${getMenuStyle('Settings').icon}`} />
                                {!isSidebarCollapsed && <span>Settings</span>}
                              </div>
                              {!isSidebarCollapsed && (
                                <svg
                                  className={`w-4 h-4 transition-transform ${leftNavSubMenus['Settings'] ? 'rotate-180' : ''}`}
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  viewBox="0 0 24 24"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                </svg>
                              )}
                            </button>
                            {leftNavSubMenus['Settings'] && !isSidebarCollapsed && (
                              <div className="pl-6 space-y-1">
                                {ADMIN_MENU_ITEMS.filter(item => {
                                  // If item has submenu, check if user has access to any nested item
                                  if (item.submenu) {
                                    return hasAnySubmenuPermission(userPermissions, item.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                                  }
                                  // Otherwise check the item's own permission
                                  return hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                                }).map(item => (
                                  item.submenu ? (
                                    // Level 3 menu item under Settings (has submenu)
                                    <div key={item.label} className="space-y-1">
                                      <button
                                        onClick={(e) => handleMenuClick(e, `Settings.${item.label}`)}
                                        className={`flex items-center justify-between w-full text-left px-3 py-2 text-sm rounded-md transition-colors text-slate-600 hover:bg-slate-50`}
                                      >
                                        <div className="flex items-center gap-2">
                                          <span className={`w-1.5 h-1.5 rounded-full ${getMenuStyle('Settings').bullet}`}></span>
                                          <span>{item.label}</span>
                                        </div>
                                        <svg
                                          className={`w-3 h-3 transition-transform ${leftNavSubMenus[`Settings.${item.label}`] ? 'rotate-180' : ''}`}
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                      {leftNavSubMenus[`Settings.${item.label}`] && (
                                        <div className="pl-6 space-y-1">
                                          {item.submenu.filter(subItem => hasPermission(userPermissions, subItem.permission, currentUserRoles, permissionsLoaded)).map(subItem => {
                                            const isActive = location.pathname === subItem.to;
                                            return (
                                              <div
                                                key={subItem.label}
                                                className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle('Settings').subActive : 'text-slate-600 hover:bg-slate-50'}`}
                                              >
                                                <button
                                                  onClick={(e) => handleNavigation(e, subItem.to)}
                                                  onMouseDown={(e) => e.button === 1 && handleNavigation(e, subItem.to)}
                                                  onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, subItem.to); }}
                                                  className="flex items-center gap-2 min-w-0 flex-1 text-left px-3 py-2 text-sm"
                                                >
                                                  <span className={`w-1 h-1 rounded-full ${getMenuStyle('Settings').bullet}`}></span>
                                                  <span className="truncate">{subItem.label}</span>
                                                </button>
                                                {renderMenuPinButton(subItem.to, { activeRow: isActive })}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // Level 2 menu item under Settings (no submenu)
                                    (() => {
                                      const isActive = location.pathname === item.to;
                                      return (
                                        <div
                                          key={item.label}
                                          className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle('Settings').subActive : 'text-slate-600 hover:bg-slate-50'}`}
                                        >
                                          <button
                                            onClick={(e) => handleNavigation(e, item.to)}
                                            onMouseDown={(e) => e.button === 1 && handleNavigation(e, item.to)}
                                            onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, item.to); }}
                                            className="flex items-center gap-2 min-w-0 flex-1 text-left px-3 py-2 text-sm"
                                          >
                                            <span className={`w-1.5 h-1.5 rounded-full ${getMenuStyle('Settings').bullet}`}></span>
                                            <span className="truncate">{item.label}</span>
                                          </button>
                                          {renderMenuPinButton(item.to, { activeRow: isActive })}
                                        </div>
                                      );
                                    })()
                                  )
                                ))}
                              </div>
                            )}
                            {isSidebarCollapsed && clickedMenuItem === 'Settings' && (
                              <div
                                className="submenu-container fixed w-56 bg-white border border-slate-200 rounded-lg shadow-xl py-2 max-h-[80vh] overflow-y-auto"
                                style={{
                                  zIndex: 99999,
                                  top: `${submenuPosition.top}px`,
                                  left: `${submenuPosition.left}px`
                                }}
                              >
                                {ADMIN_MENU_ITEMS.filter(item => {
                                  // If item has submenu, check if user has access to any nested item
                                  if (item.submenu) {
                                    return hasAnySubmenuPermission(userPermissions, item.submenu, currentUserRoles, permissionsLoaded, meetingPermissions);
                                  }
                                  // Otherwise check the item's own permission
                                  return hasPermission(userPermissions, item.permission, currentUserRoles, permissionsLoaded, meetingPermissions);
                                }).map(item => (
                                  item.submenu ? (
                                    // Level 3 menu item under Settings (has submenu) - in collapsed mode
                                    <div key={item.label} className="space-y-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleMenuClick(e, `Settings.${item.label}`);
                                        }}
                                        className={`flex items-center justify-between w-full text-left px-4 py-2 text-sm rounded-md transition-colors text-slate-700 hover:bg-slate-100`}
                                      >
                                        <span>{item.label}</span>
                                        <svg
                                          className={`w-3 h-3 transition-transform ${leftNavSubMenus[`Settings.${item.label}`] ? 'rotate-180' : ''}`}
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          viewBox="0 0 24 24"
                                        >
                                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                      </button>
                                      {leftNavSubMenus[`Settings.${item.label}`] && (
                                        <div className="pl-4 space-y-1">
                                          {item.submenu.filter(subItem => hasPermission(userPermissions, subItem.permission, currentUserRoles, permissionsLoaded)).map(subItem => {
                                            const isActive = location.pathname === subItem.to;
                                            return (
                                              <div
                                                key={subItem.label}
                                                className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle('Settings').subActive : 'text-slate-700 hover:bg-slate-100'}`}
                                              >
                                                <button
                                                  onClick={(e) => handleNavigation(e, subItem.to)}
                                                  onMouseDown={(e) => e.button === 1 && handleNavigation(e, subItem.to)}
                                                  onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, subItem.to); }}
                                                  className="flex items-center gap-2 min-w-0 flex-1 text-left px-4 py-2 text-sm"
                                                >
                                                  <span className="text-xs">•</span>
                                                  <span className="truncate">{subItem.label}</span>
                                                </button>
                                                {renderMenuPinButton(subItem.to, { activeRow: isActive, allowCollapsed: true })}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // Level 2 menu item under Settings (no submenu) - in collapsed mode
                                    (() => {
                                      const isActive = location.pathname === item.to;
                                      return (
                                        <div
                                          key={item.label}
                                          className={`group flex items-center rounded-md transition-colors ${isActive ? getMenuStyle('Settings').subActive : 'text-slate-700 hover:bg-slate-100'}`}
                                        >
                                          <button
                                            onClick={(e) => handleNavigation(e, item.to)}
                                            onMouseDown={(e) => e.button === 1 && handleNavigation(e, item.to)}
                                            onContextMenu={(e) => { e.preventDefault(); handleNavigation(e, item.to); }}
                                            className="flex items-center gap-2 min-w-0 flex-1 text-left px-4 py-2 text-sm"
                                          >
                                            <span className="truncate">{item.label}</span>
                                          </button>
                                          {renderMenuPinButton(item.to, { activeRow: isActive, allowCollapsed: true })}
                                        </div>
                                      );
                                    })()
                                  )
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </nav>
                      {/* Sidebar footer with user info — pinned below scrollable nav */}
                      {isLoggedIn && (
                        <div className="border-t border-slate-200 p-3 shrink-0 mt-auto bg-white dark:bg-slate-900">
                          {isSidebarCollapsed ? (
                            // Collapsed state: only user icon, clickable for popover
                            <div className="relative">
                              <button
                                onClick={handleUserInfoClick}
                                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors overflow-hidden"
                              >
                                {currentUserProfilePic ? (
                                  <img src={currentUserProfilePic} alt={currentUsername || ''} className="w-full h-full object-cover" />
                                ) : (
                                  <UserIcon className="w-5 h-5 text-slate-600" />
                                )}
                              </button>
                              {/* User info popover */}
                              {clickedMenuItem === 'UserInfo' && (
                                <div
                                  className="submenu-container fixed w-64 bg-white border border-slate-200 rounded-lg shadow-xl py-3"
                                  style={{
                                    zIndex: 99999,
                                    top: `${submenuPosition.top - 200}px`, // Position above the user icon
                                    left: `${submenuPosition.left}px`
                                  }}
                                >
                                  <div className="px-4 py-2 border-b border-slate-200">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden">
                                        {currentUserProfilePic ? (
                                          <img src={currentUserProfilePic} alt={currentUsername || ''} className="w-full h-full object-cover" />
                                        ) : (
                                          <UserIcon className="w-5 h-5 text-slate-600" />
                                        )}
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-sm font-semibold text-slate-800">{currentUserName || currentUsername}</span>
                                        <span className="text-xs text-slate-500">{currentUserRoles[0]}</span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="px-2 py-2 space-y-1">
                                    <button
                                      onClick={() => navigate('/profile')}
                                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                                    >
                                      <svg className="w-4 h-4 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" />
                                      </svg>
                                      <span>View Profile</span>
                                    </button>
                                    <button
                                      onClick={() => handleLogout()}
                                      className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                    >
                                      <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" />
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 17l5-5-5-5M15 12H3" />
                                      </svg>
                                      <span>Logout</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            // Expanded state: full user info with buttons
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  {currentUserProfilePic ? (
                                    <img src={currentUserProfilePic} alt={currentUsername || ''} className="w-full h-full object-cover" />
                                  ) : (
                                    <UserIcon className="w-5 h-5 text-slate-600" />
                                  )}
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <span className="text-sm font-semibold text-slate-800 truncate">{currentUserName || currentUsername}</span>
                                  <span className="text-[11px] text-slate-500 truncate">{currentUserRoles[0]}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button title="View Profile" onClick={() => navigate('/profile')} className="p-2 rounded-md hover:bg-slate-100">
                                  <svg className="w-4 h-4 text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5" /><path strokeLinecap="round" strokeLinejoin="round" d="M18.5 2.5a2.121 2.121 0 113 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                </button>
                                <button title="Logout" onClick={() => handleLogout()} className="p-2 rounded-md hover:bg-red-50">
                                  <svg className="w-4 h-4 text-red-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" /><path strokeLinecap="round" strokeLinejoin="round" d="M10 17l5-5-5-5M15 12H3" /></svg>
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </aside>
                  {/* Above page content, below modal overlays (typically z-50) */}
                  <button
                    type="button"
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    className={`hidden lg:flex fixed top-[88px] z-30 h-8 w-8 items-center justify-center rounded-full border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-md hover:shadow-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-[left] duration-200 ${
                      isSidebarCollapsed ? 'left-[48px]' : 'left-[240px]'
                    }`}
                    title={isSidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                    aria-label={isSidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
                  >
                    <svg
                      className="w-4 h-4 text-slate-600 dark:text-slate-200"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.25"
                      viewBox="0 0 24 24"
                    >
                      {isSidebarCollapsed ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                      )}
                    </svg>
                  </button>
                  <main
                    className={`flex-1 relative min-h-[calc(100vh-64px)] min-w-0 ${
                      location.pathname === '/email'
                        ? 'p-0 overflow-hidden bg-[#f0f4f9] dark:bg-slate-950'
                        : 'p-6 overflow-y-auto bg-slate-50 dark:bg-slate-900'
                    }`}
                  >
                    <Suspense fallback={<RouteFallback />}>
                    <Routes>
                      <Route path="/" element={<Navigate to={getDefaultRoute()} />} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="/dashboard" element={canViewDashboard ? <Dashboard onViewClient={clientId => navigate(`/clients/${clientId}`)} /> : <Navigate to={getDefaultRoute()} />} />
                      <Route path="/superadmin-dashboard" element={canViewSuperadminDashboard ? <SuperadminDashboard /> : <Navigate to={getDefaultRoute()} />} />
                      <Route path="/comparison-graphs" element={canViewSuperadminDashboard ? <ComparisonGraphs /> : <Navigate to={getDefaultRoute()} />} />
                      <Route path="/reports/agent-performance" element={canViewSuperadminDashboard ? <AgentPerformanceReport /> : <Navigate to={getDefaultRoute()} />} />
                      <Route path="/reports/accounts-analysis" element={currentUserRoles.includes('Superadmin') ? <CEODashboard /> : <Navigate to={getDefaultRoute()} />} />
                      <Route path="/reports/ceo-dashboard" element={<Navigate to="/reports/accounts-analysis" replace />} />
                      <Route path="/reports/owner-dashboard" element={canViewOwnerDashboard ? <OwnerDashboardPage /> : <Navigate to={getDefaultRoute()} />} />
                      <Route path="/investor-dashboard" element={canViewInvestorDashboard ? <InvestorDashboard /> : <Navigate to={getDefaultRoute()} />} />
                      <Route path="/clients/dashboard" element={<Navigate to="/clients/retentions" replace />} />
                      <Route path="/clients/retentions" element={
                        hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ?
                          <ClientsRetentionsPage
                            isSalesAgent={isSalesAgent}
                            onViewClient={clientId => navigate(`/clients/${clientId}`)}
                          /> :
                          <Navigate to={getDefaultRoute()} />
                      } />
                      <Route path="/clients" element={
                        !sessionChecked || !currentUsername ?
                          <div className="p-8 text-center text-slate-500">Loading...</div> :
                          hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ?
                            <ClientsPage isSalesAgent={isSalesAgent} currentUsername={currentUsername} onViewClient={clientId => navigate(`/clients/${clientId}`)} onAddNewClient={() => navigate('/clients/new')} /> :
                            <Navigate to="/" />
                      } />
                      <Route path="/clients/v2" element={
                        !sessionChecked || !currentUsername ?
                          <div className="p-8 text-center text-slate-500">Loading...</div> :
                          hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ?
                            <ClientsV2Page isSalesAgent={isSalesAgent} currentUsername={currentUsername} /> :
                            <Navigate to="/" />
                      } />
                      <Route path="/clients/v2/status-check" element={
                        !sessionChecked || !currentUsername ?
                          <div className="p-8 text-center text-slate-500">Loading...</div> :
                          hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ?
                            <ClientsV2StatusCheckPage /> :
                            <Navigate to="/" />
                      } />
                      <Route path="/agents" element={
                        !sessionChecked || !currentUsername ?
                          <div className="p-8 text-center text-slate-500">Loading...</div> :
                          hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ?
                            <AgentsPage /> :
                            <Navigate to="/" />
                      } />
                      <Route path="/agents/:id" element={
                        !sessionChecked || !currentUsername ?
                          <div className="p-8 text-center text-slate-500">Loading...</div> :
                          hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ?
                            <AgentDetailPage /> :
                            <Navigate to="/" />
                      } />
                      <Route path="/clients/new" element={hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ClientDetailsModal client={{ id: '', name: '', email: '', phone: '', company: '', companyId: 'secondary', status: ClientStatus.Onboarding, numberOfUsers: 1, onboardingTasks: { hasWebsite: false, websiteDelivered: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, hasSalesFunnel: false, salesFunnelDelivered: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, adminTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, agentTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, websiteTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' } }, qualityCheckRemarks: '', clientFeedback: '', satisfactionLevel: null, renewalPrediction: null, createdAt: '', modifiedAt: '', attachments: [] }} isOpen={true} onClose={() => navigate('/clients')} onSave={handleSaveClient} currentUserRoles={currentUserRoles} onTaskStatusUpdateRequest={handleTaskStatusUpdateRequest} currentUsername={currentUsername} error={clientSaveError} /> : <Navigate to="/" />} />
                      <Route path="/clients/new-wizard" element={hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'create' }, currentUserRoles, permissionsLoaded) ? <CreateClientWizardPage currentUserRoles={currentUserRoles} currentUsername={currentUsername} /> : <Navigate to="/" />} />
                      <Route path="/clients/:id/edit-wizard" element={hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'edit' }, currentUserRoles, permissionsLoaded) ? <CreateClientWizardPage currentUserRoles={currentUserRoles} currentUsername={currentUsername} /> : <Navigate to="/" />} />
                      <Route path="/clients/:clientId/attachments/:attachmentId/view" element={hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ContractAttachmentViewerPage /> : <Navigate to="/" />} />
                      <Route path="/clients/:id" element={hasPermission(userPermissions, { module: 'Clients', section: 'ClientList', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ClientDetailWrapper clients={clients} activityLogs={activityLogs} currentUserRoles={currentUserRoles} onEdit={handleOpenModal} currentUsername={currentUsername} currentUserName={currentUserName} onDelete={handleDeleteClient} /> : <Navigate to="/" />} />
                      <Route path="/calendar" element={hasPermission(userPermissions, { module: 'Calendar', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <CalendarPage clients={clients} onViewClient={handleViewClient} currentUserRoles={currentUserRoles} /> : <Navigate to="/" />} />
                      <Route path="/activity" element={hasPermission(userPermissions, { module: 'Activity', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ActivityPage activityLogs={activityLogs} onViewClient={clientId => navigate(`/clients/${clientId}`)} /> : <Navigate to="/" />} />
                      <Route path="/userManagement" element={hasPermission(userPermissions, { module: 'UserManagement', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AdminUserManagement currentUserRoles={currentUserRoles} /> : <Navigate to="/" />} />
                      <Route path="/userSalesSummary" element={hasPermission(userPermissions, { module: 'UserManagement', section: 'UserSalesSummary', action: 'view' }, currentUserRoles, permissionsLoaded) ? <UserSalesSummary users={users} clients={clients} /> : <Navigate to="/" />} />
                      <Route path="/sales-summary-v2" element={hasPermission(userPermissions, { module: 'UserManagement', section: 'UserSalesSummary', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SalesSummaryV2 users={users} clients={clients} /> : <Navigate to="/" />} />
                      <Route path="/payment-analytics" element={hasPermission(userPermissions, { module: 'Reports', section: 'PaymentAnalytics', action: 'view' }, currentUserRoles, permissionsLoaded) ? <PaymentAnalyticsV2 /> : <Navigate to="/" />} />
                      <Route path="/payment-analytics-v2" element={hasPermission(userPermissions, { module: 'Reports', section: 'PaymentAnalytics', action: 'view' }, currentUserRoles, permissionsLoaded) ? <PaymentAnalyticsV2 /> : <Navigate to="/" />} />
                      <Route path="/payment-analytics-legacy" element={hasPermission(userPermissions, { module: 'Reports', section: 'PaymentAnalytics', action: 'view' }, currentUserRoles, permissionsLoaded) ? <PaymentAnalytics /> : <Navigate to="/" />} />
                      <Route path="/commissionManagement" element={hasPermission(userPermissions, { module: 'CommissionManagement', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <CommissionManagement /> : <Navigate to="/" />} />
                      <Route path="/web-manager-clients" element={hasPermission(userPermissions, { module: 'Clients', section: 'WebClients', action: 'view' }, currentUserRoles, permissionsLoaded) ? <WebManagerClientsPage /> : <Navigate to="/" />} />
                      <Route path="/web-manager-clients/:clientId" element={hasPermission(userPermissions, { module: 'Clients', section: 'WebClients', action: 'view' }, currentUserRoles, permissionsLoaded) ? <WebManagerClientDetailWrapper /> : <Navigate to="/" />} />
                      <Route path="/modification-request/:requestId" element={hasPermission(userPermissions, { module: 'Clients', section: 'WebClients', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ModificationRequestDetailWrapper /> : <Navigate to="/" />} />
                      <Route path="/contracts" element={hasPermission(userPermissions, { module: 'Contracts', section: 'ContractGenerator', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ContractGenerator currentUserRoles={currentUserRoles} currentUsername={currentUsername} currentUserId={currentUserId} users={users} /> : <Navigate to="/" />} />
                      <Route path="/contracts/admin" element={hasPermission(userPermissions, { module: 'Contracts', section: 'ContractTemplateAdmin', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ContractTemplateAdmin /> : <Navigate to="/" />} />
                      <Route path="/contracts/saved" element={hasPermission(userPermissions, { module: 'Contracts', section: 'SavedContracts', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SavedContracts currentUserRoles={currentUserRoles} currentUserId={currentUserId} /> : <Navigate to="/" />} />
                      <Route path="/contracts/:id" element={hasPermission(userPermissions, { module: 'Contracts', section: 'ContractGenerator', action: 'view' }, currentUserRoles, permissionsLoaded) || hasPermission(userPermissions, { module: 'Contracts', section: 'ContractGenerator', action: 'view_all' }, currentUserRoles, permissionsLoaded) ? <ContractDetailPage currentUserRoles={currentUserRoles} currentUserId={currentUserId} /> : <Navigate to="/" />} />
                      <Route path="/proposals" element={hasPermission(userPermissions, { module: 'Contracts', section: 'ContractGenerator', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ProposalGenerator currentUserRoles={currentUserRoles} currentUsername={currentUsername} currentUserId={currentUserId} users={users} /> : <Navigate to="/" />} />
                      <Route path="/proposals/saved" element={hasPermission(userPermissions, { module: 'Contracts', section: 'SavedContracts', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SavedProposals currentUserRoles={currentUserRoles} currentUserId={currentUserId} /> : <Navigate to="/" />} />
                      <Route path="/demo-requests" element={hasPermission(userPermissions, { module: 'DemoRequests', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <DemoRequestsPage /> : <Navigate to="/" />} />
                      <Route path="/email" element={hasPermission(userPermissions, { module: 'Email', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <EmailManagement /> : <Navigate to="/" />} />
                      <Route path="/tasks" element={hasPermission(userPermissions, { module: 'Tasks', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <TasksPage /> : <Navigate to="/" />} />
                      <Route path="/success-criteria" element={hasPermission(userPermissions, { module: 'SuccessCriteria', section: 'CriteriaList', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SuccessCriteriaDashboard /> : <Navigate to="/dashboard" />} />
                      <Route path="/reports/support-report" element={hasPermission(userPermissions, { module: 'SuccessCriteria', section: 'SupportReport', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SupportReportPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/roleManagement" element={hasPermission(userPermissions, { module: 'RoleManagement', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <RoleManagement /> : <Navigate to="/" />} />
                      <Route path="/email-templates" element={<Navigate to="/email?view=settings&tab=templates" replace />} />

                      <Route path="/database-backup" element={currentUserRoles.includes('Superadmin') ? <DatabaseBackupManager /> : <Navigate to="/dashboard" />} />
                      <Route path="/whatsapp" element={hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <WhatsAppPage /> : <Navigate to="/" />} />
                      <Route path="/whatsapp/analytics" element={hasPermission(userPermissions, { module: 'WhatsAppAnalytics', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <WhatsAppAnalytics /> : <Navigate to="/" />} />
                      <Route path="/marketing/campaigns" element={hasPermission(userPermissions, { module: 'Marketing', section: 'Campaigns', action: 'view' }, currentUserRoles, permissionsLoaded) ? <CampaignListPage /> : <Navigate to="/" />} />
                      <Route path="/marketing/campaigns/:id" element={hasPermission(userPermissions, { module: 'Marketing', section: 'Campaigns', action: 'view' }, currentUserRoles, permissionsLoaded) ? <CampaignDetailPage /> : <Navigate to="/" />} />
                      <Route path="/tickets" element={hasPermission(userPermissions, { module: 'Tickets', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <TicketsPage /> : <Navigate to="/" />} />
                      <Route path="/tickets/analytics" element={hasPermission(userPermissions, { module: 'Tickets', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <TicketAnalytics /> : <Navigate to="/" />} />
                      <Route path="/tickets/settings" element={hasPermission(userPermissions, { module: 'Tickets', section: 'Settings', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <TicketSettingsPage /> : <Navigate to="/" />} />
                      <Route path="/tickets/unlinked" element={hasPermission(userPermissions, { module: 'Tickets', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <UnlinkedTicketsPage /> : <Navigate to="/" />} />
                      <Route path="/tickets/api" element={hasPermission(userPermissions, { module: 'Tickets', section: 'Settings', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <TicketApiDocPage /> : <Navigate to="/" />} />
                      <Route path="/tickets/:id" element={hasPermission(userPermissions, { module: 'Tickets', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <TicketDetailPage /> : <Navigate to="/" />} />
                      <Route path="/reports/attendance" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'HR', section: 'Attendance', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <AttendanceReportsPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />

                      {/* Settings */}
                      <Route path="/settings/ai" element={currentUserRoles.includes('Superadmin') ? <AISettingsPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/ai/query-config" element={currentUserRoles.includes('Superadmin') ? <AIQuerySettingsPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/rag" element={currentUserRoles.includes('Superadmin') ? <RAGManagementPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/company" element={
                        <CompanyProvider>
                          {currentUserRoles.includes('Superadmin') ? <CompanySettingsPage /> : <Navigate to="/dashboard" />}
                        </CompanyProvider>
                      } />
                      <Route path="/settings/companies" element={currentUserRoles.includes('Superadmin') ? <CompanySettings /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/s3-browser" element={currentUserRoles.includes('Superadmin') ? <S3BrowserPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/locations-fetcher" element={currentUserRoles.includes('Superadmin') ? <LocationsFetcherPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/system-health" element={currentUserRoles.includes('Superadmin') ? <SystemHealthPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/vpn" element={currentUserRoles.includes('Superadmin') ? <VpnAdminPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/settings/phone-parser" element={currentUserRoles.includes('Superadmin') ? <PhoneParserToolPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/development/dashboard" element={(currentUserRoles.includes('Superadmin') || hasDevTrackerAccess) ? <DevDashboard /> : <Navigate to="/dashboard" />} />
                      <Route path="/development/settings" element={currentUserRoles.includes('Superadmin') ? <DevTrackerSettings /> : <Navigate to="/dashboard" />} />
                      <Route path="/development/requests" element={(currentUserRoles.includes('Superadmin') || hasDevTrackerAccess) ? <DevTaskRequests /> : <Navigate to="/dashboard" />} />
                      <Route path="/development/board" element={(currentUserRoles.includes('Superadmin') || hasDevTrackerAccess) ? <DevKanbanBoard /> : <Navigate to="/dashboard" />} />
                      <Route path="/development/releases" element={currentUserRoles.includes('Superadmin') ? <DevReleases /> : <Navigate to="/dashboard" />} />
                      <Route path="/development/products/:productId" element={currentUserRoles.includes('Superadmin') ? <DevProductDetail /> : <Navigate to="/dashboard" />} />

                      {/* Recruitment Module */}
                      <Route path="/recruitment/candidates" element={<CandidatesPage />} />
                      <Route path="/recruitment/candidates/:id" element={<CandidateDetailPage />} />
                      <Route path="/recruitment/jobs" element={<JobPostingsPage />} />
                      <Route path="/recruitment/pipeline" element={<RecruitmentPipelinePage />} />
                      <Route path="/recruitment/reports" element={<RecruitmentReportsPage />} />
                      <Route path="/recruitment/settings" element={<RecruitmentSettingsPage />} />

                      {/* Stripe Module Routes */}
                      <Route path="/stripe/settings" element={hasPermission(userPermissions, { module: 'Stripe', section: 'Settings', action: 'view' }, currentUserRoles, permissionsLoaded) ? <StripeSettingsPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/stripe/subscriptions" element={hasPermission(userPermissions, { module: 'Stripe', section: 'Subscriptions', action: 'view' }, currentUserRoles, permissionsLoaded) ? <StripeSubscriptionsPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/stripe/customers" element={hasPermission(userPermissions, { module: 'Stripe', section: 'Customers', action: 'view' }, currentUserRoles, permissionsLoaded) ? <StripeCustomersPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/stripe/transactions" element={hasPermission(userPermissions, { module: 'Stripe', section: 'Transactions', action: 'view' }, currentUserRoles, permissionsLoaded) ? <StripeTransactionsPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/stripe/payment-links" element={hasPermission(userPermissions, { module: 'Stripe', section: 'PaymentLinks', action: 'view' }, currentUserRoles, permissionsLoaded) ? <StripePaymentLinksPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/stripe/reconciliation" element={hasPermission(userPermissions, { module: 'Stripe', section: 'Reconciliation', action: 'view' }, currentUserRoles, permissionsLoaded) || hasPermission(userPermissions, { module: 'Stripe', section: 'Transactions', action: 'view' }, currentUserRoles, permissionsLoaded) ? <StripeReconciliationPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/stripe/billing-orphans" element={hasPermission(userPermissions, { module: 'Stripe', section: 'Transactions', action: 'view' }, currentUserRoles, permissionsLoaded) || hasPermission(userPermissions, { module: 'Stripe', section: 'Reconciliation', action: 'view' }, currentUserRoles, permissionsLoaded) ? <StripeBillingOrphansPage /> : <Navigate to="/dashboard" />} />

                      {/* VOIP Module Routes */}
                      <Route path="/voip" element={<Navigate to="/voip/calls" replace />} />
                      <Route path="/voip/calls" element={hasPermission(userPermissions, { module: 'VOIP', section: 'CallLog', action: 'view' }, currentUserRoles, permissionsLoaded) ? <VoipCallLogsPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/voip/agents" element={hasPermission(userPermissions, { module: 'VOIP', section: 'AgentLog', action: 'view' }, currentUserRoles, permissionsLoaded) ? <VoipAgentLogsPage /> : <Navigate to="/dashboard" />} />

                      {/* Subscriptions Module Routes */}
                      <Route path="/subscriptions" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SubscriptionsDashboard /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/new" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Subscriptions', action: 'create' }, currentUserRoles, permissionsLoaded) ? <NewSubscription /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/subscriptions" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Subscriptions', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ClientSubscriptionsPage /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/audit" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Subscriptions', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ActiveSubscriptionsAudit /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/subscriptions/:id" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Subscriptions', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ClientSubscriptionDetail /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/packages" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Packages', action: 'view' }, currentUserRoles, permissionsLoaded) ? <PackagesManagement /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/addons" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Addons', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AddonsManagement /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/coupons" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Coupons', action: 'view' }, currentUserRoles, permissionsLoaded) ? <CouponsManagement /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/reports" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Reports', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SubscriptionReports /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/analytics" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Reports', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SubscriptionAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/crm-links" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'Reports', action: 'view' }, currentUserRoles, permissionsLoaded) ? <CrmLinks /> : <Navigate to="/dashboard" />} />
                      <Route path="/subscriptions/api" element={hasPermission(userPermissions, { module: 'Subscriptions', section: 'API', action: 'view' }, currentUserRoles, permissionsLoaded) ? <APIManagement currentUserRoles={currentUserRoles} /> : <Navigate to="/dashboard" />} />

                      {/* CRM Analytics Module Routes */}
                      <Route path="/crm-analytics/usage" element={currentUserRoles.includes('Superadmin') ? <CrmUsageAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/crm-analytics/leads" element={currentUserRoles.includes('Superadmin') ? <CrmLeadAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/crm-analytics/communication" element={currentUserRoles.includes('Superadmin') ? <CrmCommunicationAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/crm-analytics/properties" element={currentUserRoles.includes('Superadmin') ? <CrmPropertyAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/crm-analytics/subscriptions" element={currentUserRoles.includes('Superadmin') ? <CrmSubscriptionAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/crm-analytics/search" element={currentUserRoles.includes('Superadmin') ? <CrmSearchAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/crm-analytics/teams" element={currentUserRoles.includes('Superadmin') ? <CrmTeamAnalytics /> : <Navigate to="/dashboard" />} />
                      <Route path="/crm-analytics/growth" element={currentUserRoles.includes('Superadmin') ? <CrmGrowthAnalytics /> : <Navigate to="/dashboard" />} />


                      <Route path="/hr/attendance" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'HR', section: 'Attendance', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AttendanceDashboardPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/hr/attendance/records" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'HR', section: 'Attendance', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AttendanceRecordsPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/hr/attendance/reports" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'HR', section: 'Attendance', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AttendanceReportsPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/hr/attendance/settings" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'HR', section: 'Attendance', action: 'manage_rules' }, currentUserRoles, permissionsLoaded) ? <AttendanceSettingsPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/hr/attendance/time-requests" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'HR', section: 'Attendance', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AttendanceTimeChangeRequestsPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />

                      {/* Salaries Routes - Wrapped in CompanyProvider for multi-company support */}
                      <Route path="/salaries/dashboard" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'Salaries', section: 'SalaryList', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SalaryDashboardPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/salaries" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'Salaries', section: 'SalaryList', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SalaryListPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/salaries/config" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'Salaries', section: 'Config', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SalaryConfigPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/salaries/advances" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'Salaries', section: 'Advances', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AdvancesPage /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />
                      <Route path="/salaries/accounting" element={
                        <CompanyProvider>
                          {hasPermission(userPermissions, { module: 'Salaries', section: 'Accounting', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SalaryAccountingSettings /> : <Navigate to="/" />}
                        </CompanyProvider>
                      } />



                      {/* Meeting Distribution Routes */}
                      <Route path="/meetings/agents" element={hasPermission(userPermissions, { module: 'Meetings', section: 'AgentSettings', action: 'view' }, currentUserRoles, permissionsLoaded) ? <MeetingAgentSettingsPage /> : <Navigate to="/" />} />
                      <Route path="/meetings/calendar" element={hasPermission(userPermissions, { module: 'Meetings', section: 'Calendar', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <MeetingsCalendarView /> : <Navigate to="/" />} />
                      <Route path="/meetings/distribute" element={hasPermission(userPermissions, { module: 'Meetings', section: 'Distribution', action: 'create' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <MeetingDistributionPage /> : <Navigate to="/" />} />
                      <Route path="/meetings/book" element={hasPermission(userPermissions, { module: 'Meetings', section: 'Distribution', action: 'create' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <MeetingBookingPage /> : <Navigate to="/" />} />
                      <Route path="/meetings/assign" element={hasPermission(userPermissions, { module: 'Meetings', section: 'Distribution', action: 'manual_assign' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <AssignMeetingsPage /> : <Navigate to="/" />} />
                      <Route path="/meetings/my-meetings" element={hasPermission(userPermissions, { module: 'Meetings', section: 'Distribution', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <MyMeetingsPage /> : <Navigate to="/" />} />
                      <Route path="/meetings/permissions" element={currentUserRoles.includes('Superadmin') ? <MeetingPermissionsPage /> : <Navigate to="/" />} />
                      <Route path="/meetings/:id" element={hasPermission(userPermissions, { module: 'Meetings', section: 'Calendar', action: 'view' }, currentUserRoles, permissionsLoaded, meetingPermissions) ? <MeetingDetailPage /> : <Navigate to="/" />} />

                      {/* Support Automation Routes */}
                      <Route path="/support-automation/flows" element={hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SupportAutomationFlowBuilder /> : <Navigate to="/" />} />
                      <Route path="/support-automation/agents" element={hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SupportAgentsList /> : <Navigate to="/" />} />
                      <Route path="/support-automation/clients" element={hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SupportAutomationClientConfig /> : <Navigate to="/" />} />
                      <Route path="/support-automation/settings" element={hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SupportAutomationSettings /> : <Navigate to="/" />} />
                      <Route path="/support-automation/history" element={<Navigate to="/reports/support-conversations" replace />} />
                      <Route path="/reports/support-conversations" element={hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SupportConversationHistory /> : <Navigate to="/" />} />

                      {/* WhatsApp Support Route - Filtered view for automation-enabled clients */}
                      <Route path="/whatsapp-support" element={hasPermission(userPermissions, { module: 'WhatsApp', section: 'Main', action: 'view' }, currentUserRoles, permissionsLoaded) ? <WhatsAppSupportPage /> : <Navigate to="/" />} />

                      {/* Accounting Module Routes - Wrapped in CompanyProvider for multi-company support */}
                      <Route path="/accounting/*" element={
                        <CompanyProvider>
                          <Routes>
                            <Route path="dashboard" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewDashboard' }, currentUserRoles, permissionsLoaded) ? <AccountingDashboard /> : <Navigate to="/" />} />
                            <Route path="accounts" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Accounts', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ChartOfAccounts /> : <Navigate to="/" />} />
                            <Route path="journal-entries" element={hasPermission(userPermissions, { module: 'Accounting', section: 'JournalEntries', action: 'view' }, currentUserRoles, permissionsLoaded) ? <JournalEntries /> : <Navigate to="/" />} />
                            <Route path="special-transactions" element={hasPermission(userPermissions, { module: 'Accounting', section: 'JournalEntries', action: 'view' }, currentUserRoles, permissionsLoaded) ? <SpecialTransactions /> : <Navigate to="/" />} />
                            <Route path="income" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Income', action: 'view' }, currentUserRoles, permissionsLoaded) ? <IncomeRecording /> : <Navigate to="/" />} />
                            <Route path="expenses" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Expenses', action: 'view' }, currentUserRoles, permissionsLoaded) ? <ExpenseRecording /> : <Navigate to="/" />} />
                            <Route path="ar-invoices" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewARInvoices' }, currentUserRoles, permissionsLoaded) ? <ARInvoices /> : <Navigate to="/" />} />
                            <Route path="profit-loss" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewProfitLoss' }, currentUserRoles, permissionsLoaded) ? <ProfitLossStatement /> : <Navigate to="/" />} />
                            <Route path="reconciliation" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewProfitLoss' }, currentUserRoles, permissionsLoaded) ? <ReconciliationReport /> : <Navigate to="/" />} />
                            <Route path="daily-activity" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewDailyActivity' }, currentUserRoles, permissionsLoaded) ? <DailyActivityReport /> : <Navigate to="/" />} />
                            <Route path="balance-sheet" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewBalanceSheet' }, currentUserRoles, permissionsLoaded) ? <BalanceSheet /> : <Navigate to="/" />} />
                            <Route path="income-statement" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewIncomeStatement' }, currentUserRoles, permissionsLoaded) ? <IncomeStatement /> : <Navigate to="/" />} />
                            <Route path="trial-balance" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewTrialBalance' }, currentUserRoles, permissionsLoaded) ? <TrialBalance /> : <Navigate to="/" />} />
                            <Route path="general-ledger" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'viewGeneralLedger' }, currentUserRoles, permissionsLoaded) ? <GeneralLedger /> : <Navigate to="/" />} />
                            <Route path="currencies" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Reports', action: 'view' }, currentUserRoles, permissionsLoaded) ? <CurrencyManagement /> : <Navigate to="/" />} />
                            <Route path="periods" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Periods', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AccountingPeriods /> : <Navigate to="/" />} />
                            <Route path="permissions" element={hasPermission(userPermissions, { module: 'Accounting', section: 'Permissions', action: 'view' }, currentUserRoles, permissionsLoaded) ? <AccountingPermissions /> : <Navigate to="/" />} />
                          </Routes>
                        </CompanyProvider>
                      } />

                      {/* Add other routes as needed */}
                    </Routes>
                    </Suspense>
                  </main>
                </div>
              </div>
              {isModalOpen && (
                <ClientDetailsModal
                  client={selectedClient || { id: '', name: '', email: '', phone: '', company: '', status: ClientStatus.Onboarding, numberOfUsers: 1, onboardingTasks: { hasWebsite: false, websiteDelivered: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, hasSalesFunnel: false, salesFunnelDelivered: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, adminTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, agentTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' }, websiteTrainingCompleted: { status: TaskStatus.Pending, remarks: [], scheduledDate: null, scheduledBy: '' } }, qualityCheckRemarks: '', clientFeedback: '', satisfactionLevel: null, renewalPrediction: null, createdAt: '', modifiedAt: '', attachments: [] }}
                  isOpen={isModalOpen}
                  onClose={handleCloseModal}
                  onSave={handleSaveClient}
                  currentUserRoles={currentUserRoles}
                  onTaskStatusUpdateRequest={handleTaskStatusUpdateRequest}
                  currentUsername={currentUsername}
                  error={clientSaveError}
                />
              )}
              {remarkModalState.isOpen && (
                <RemarkModal
                  isOpen={remarkModalState.isOpen}
                  action={remarkModalState.action}
                  onClose={() => setRemarkModalState({ isOpen: false, taskKey: null, clientId: null, newStatus: null, action: null })}
                  onSubmit={(remarkText) => {
                    if (remarkModalState.clientId && remarkModalState.taskKey && remarkModalState.newStatus && remarkModalState.action) {
                      handleConfirmTaskStatusUpdate(remarkModalState.clientId, remarkModalState.taskKey, remarkModalState.newStatus, remarkModalState.action, remarkText);
                    }
                  }}
                />
              )}
              {showProfileModal && (
                <Modal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)}>
                  <ChangePasswordForm onChangePassword={handleChangePassword} onClose={() => setShowProfileModal(false)} />
                </Modal>
              )}

              {/* Version Checker - Check for updates every 5 minutes */}
              <VersionChecker checkInterval={5 * 60 * 1000} autoReloadDelay={10} />

              {/* AI Assistant Chat Interface (Superadmin only) */}
              {currentUserRoles.includes('Superadmin') && (
                <Suspense fallback={null}>
                <AIChatInterface
                  isOpen={isAIChatOpen}
                  onClose={() => setIsAIChatOpen(false)}
                  onMinimize={() => setIsAIChatMinimized(true)}
                />
                </Suspense>
              )}

              <RemVoiceDialer />
            </div>
            </RemVoiceProvider>
          </ThemeProvider>
        </CurrencyProvider>
      </PermissionProvider>
    </ToastProvider>
  );
};

export default App;